/* ===== Nicetravel Calendar Widget — debounced remote availability, no cache =====
   Changes from previous version:
   - Today + all past days are disabled; today is visually highlighted.
     Exception: a product whose /availability says same_day_booking keeps
     today selectable while the API reports it available (spec 2026-09-17 §4).
   - Can't navigate to past months (Prev disabled on current month).
   - No in-memory cache; re-fetch availability every time.
   - 2s debounce before fetching availability on month changes.
============================================================================= */

(function () {
    // Total number of calendar months visible (including the current month)
    const MAX_VISIBLE_MONTHS = 18;

    // Canonical display order for person types (Adults, Teenagers, Children)
    const PERSON_TYPE_DISPLAY_ORDER = { adults: 0, teenager: 1, children: 2 };

    // Inline-list default (the Nice Travel directory's "Not Known Yet").
    const DEFAULT_PICKUP_LOCATION = 32465;
    const DEFAULT_PICKUP_LOCATION_LABEL = 'Not Known Yet (Will Provide Details Later)';

    const NiceCalendar = {
      _state: {
        today: new Date(),
        viewYear: null,      // 4-digit
        viewMonth: null,     // 0..11
        selectedDate: null,
        personCounts: {}, // Dynamic person field counts
        pickupLocation: DEFAULT_PICKUP_LOCATION, // Default pickup location
        pickupLocationLabel: DEFAULT_PICKUP_LOCATION_LABEL, // Default pickup location label
        pickupFromServedList: false, // Did the selection come from a served (third-party) list?
        pickupChosenByUser: false, // Did the customer pick the place (vs. an auto-selected default)?
        dropoffLocation: null, // Chosen dropoff place id; served lists only, no default (spec 2026-09-17 §3)
        dropoffLocationLabel: '',
        dropoffChosenByUser: false, // Did the customer pick it (vs. auto-selected first entry)?
        // Once-per-page latches for warnings raised on every render.
        _pickupRequiredWarned: false,
        _depositRuleWarned: false,
        _cartUrlWarned: false,
        depositOption: 'full', // Default to full payment
        config: null,
  
        // availability for the currently visible month
        dayAvailMap: new Map(),      // ymd -> boolean
        dayCapacityMap: new Map(),   // ymd -> number|null
        dayStartTimesMap: new Map(), // ymd -> start_times[] array from API
        selectedStartTime: null,     // Selected start time slot { activity_id, start_time_id, time, capacity }
  
        // loading / debounce
        loading: false,
        bookingLoading: false,
        fetchTimer: null,
        pendingYear: null,
        pendingMonth0: null,
        // flag to ensure auto-jump is only used on initial load
        initialAutoSearchDone: false,
        
        // currency monitoring
        currencyTimer: null,
        lastCurrency: null,
        lastCurrencyRate: null,

        // base prices per person field (before any booking pricing adjustments)
        basePersonPrices: {},

        _hostEl: null,
      },
  
      // --- Utils ---
      _daysInMonth(y, m0) { return new Date(y, m0 + 1, 0).getDate(); },
      _pad(n) { return n < 10 ? '0' + n : '' + n; },
      _ymd(y, m0, d) { return `${y}-${this._pad(m0+1)}-${this._pad(d)}`; },
      _midnight(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; },
      _isPast(y, m0, d) {
        const test = this._midnight(new Date(y, m0, d)).getTime();
        const todayMid = this._midnight(this._state.today).getTime();
        return test < todayMid;
      },
      _isToday(y, m0, d) {
        const t = this._state.today;
        return y === t.getFullYear() && m0 === t.getMonth() && d === t.getDate();
      },
      _isMoreThan30DaysFuture(date) {
        const today = this._state.today;
        const thirtyDaysFromNow = new Date(today);
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        return date.getTime() > thirtyDaysFromNow.getTime();
      },
      _getActiveBookingPricingRuleForDate(date) {
        const cfg = this._state.config || {};
        const pricing = cfg.bookingPricing;
        if (!pricing || !Array.isArray(pricing.pricing_ranges) || pricing.pricing_ranges.length === 0) {
          return null;
        }

        const ymd = this._toYMD(date);

        const matches = pricing.pricing_ranges.filter((rule) => {
          if (!rule) return false;

          const from = rule.from || null;
          const to = rule.to || null;

          if (!from && !to) return false;
          if (from && to) return from <= ymd && ymd <= to;
          if (from && !to) return from <= ymd;
          if (!from && to) return ymd <= to;
          return false;
        });

        if (!matches.length) return null;

        // Deterministic choice: last matching rule wins
        return matches[matches.length - 1];
      },
      _applyBookingRuleToPrice(basePrice, rule) {
        if (!rule) return basePrice;

        const applyModifier = (current, amount, modifier) => {
          if (typeof amount !== 'number' || !isFinite(amount)) return current;
          const op = (modifier || 'plus').toString().toLowerCase();
          if (op === 'plus' || op === '+') return current + amount;
          if (op === 'minus' || op === '-') return current - amount;
          if (op === 'times' || op === 'x' || op === '*') return current * amount;
          if (op === 'divide' || op === '/') return amount === 0 ? current : current / amount;
          return current;
        };

        let price = basePrice;

        const baseDelta = typeof rule.base_cost === 'number'
          ? rule.base_cost
          : (rule.base_cost !== undefined && rule.base_cost !== null && rule.base_cost !== ''
              ? parseFloat(rule.base_cost)
              : NaN);
        if (!isNaN(baseDelta)) {
          price = applyModifier(price, baseDelta, rule.base_modifier);
        }

        const costDelta = typeof rule.cost === 'number'
          ? rule.cost
          : (rule.cost !== undefined && rule.cost !== null && rule.cost !== ''
              ? parseFloat(rule.cost)
              : NaN);
        if (!isNaN(costDelta)) {
          price = applyModifier(price, costDelta, rule.modifier);
        }

        // Clamp at 0 and round to integer ISK
        return Math.max(0, Math.round(price));
      },
      _getEffectivePersonPrice(field) {
        const baseMap = this._state.basePersonPrices || {};
        const base = (baseMap && baseMap[field.fieldId] != null)
          ? baseMap[field.fieldId]
          : (parseInt(field.price) || 0);

        const s = this._state;
        if (!s.selectedDate) return base;

        const rule = this._getActiveBookingPricingRuleForDate(s.selectedDate);
        if (!rule) return base;

        return this._applyBookingRuleToPrice(base, rule);
      },
      _fmtCurrency(amount) {
        // Check for currency values in cookies first, fall back to config
        const cookieCurrency = this._getCookie('nt_currency');
        const cookieCurrencyRate = this._getCookie('nt_currency_rate');
        
        const { currency = 'ISK', currencyRate = 1, locale = 'en-IS' } = this._state.config || {};
        
        // Use cookie values if available, otherwise use config values
        const finalCurrency = cookieCurrency !== null ? cookieCurrency : currency;
        const finalCurrencyRate = cookieCurrencyRate !== null ? (parseFloat(cookieCurrencyRate) || currencyRate) : currencyRate;
        
        // Convert ISK amount to target currency using the exchange rate
        const convertedAmount = amount * finalCurrencyRate;
        
        // Determine decimal places based on currency
        // ISK typically uses 0 decimal places, most other currencies use 2
        const cleanCurrency = finalCurrency.trim(); // Remove any whitespace from currency
        const maximumFractionDigits = cleanCurrency === 'ISK' ? 0 : 2;

        try { return new Intl.NumberFormat(locale, { style:'currency', currency: cleanCurrency, maximumFractionDigits }).format(convertedAmount); }
        catch { return convertedAmount.toLocaleString() + ' ' + cleanCurrency; }
      },
      // Helper function to get cookie value
      _getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for(let i = 0; i < ca.length; i++) {
          let c = ca[i];
          while (c.charAt(0) === ' ') c = c.substring(1, c.length);
          if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
      },
      
      // Check for currency cookie changes and update display
      _checkCurrencyChanges() {
        const currentCurrency = this._getCookie('nt_currency');
        const currentCurrencyRate = this._getCookie('nt_currency_rate');
        
        // Check if currency values have changed
        const currencyChanged = currentCurrency !== this._state.lastCurrency;
        const rateChanged = currentCurrencyRate !== this._state.lastCurrencyRate;
        
        if (currencyChanged || rateChanged) {
          console.log('[NiceCalendar] Currency cookies changed:', {
            currency: `${this._state.lastCurrency} → ${currentCurrency}`,
            rate: `${this._state.lastCurrencyRate} → ${currentCurrencyRate}`
          });
          
          // Update stored values
          this._state.lastCurrency = currentCurrency;
          this._state.lastCurrencyRate = currentCurrencyRate;
          
          // Re-render if calendar is initialized
          if (this._state._hostEl) {
            this._render(this._state._hostEl);
          }
        }
      },
      
      // Start monitoring currency cookies
      _startCurrencyMonitoring() {
        // Clear any existing timer
        if (this._state.currencyTimer) {
          clearInterval(this._state.currencyTimer);
        }
        
        // Initialize current values
        this._state.lastCurrency = this._getCookie('nt_currency');
        this._state.lastCurrencyRate = this._getCookie('nt_currency_rate');
        
        // Start monitoring every second
        this._state.currencyTimer = setInterval(() => {
          this._checkCurrencyChanges();
        }, 1000);
      },
      
      // Stop monitoring currency cookies
      _stopCurrencyMonitoring() {
        if (this._state.currencyTimer) {
          clearInterval(this._state.currencyTimer);
          this._state.currencyTimer = null;
        }
      },
      
      _hidden(name, value) { const i=document.createElement('input'); i.type='hidden'; i.name=name; i.value=(value==null?'':String(value)); return i; },
      
      // Generate month options for dropdown (up to MAX_VISIBLE_MONTHS in the future)
      _generateMonthOptions(currentYear, currentMonth) {
        const today = this._state.today;
        const options = [];
        
        // Generate options for the next MAX_VISIBLE_MONTHS starting from current month
        for (let i = 0; i < MAX_VISIBLE_MONTHS; i++) {
          const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
          const year = date.getFullYear();
          const month = date.getMonth();
          const monthName = date.toLocaleString(this._state.config?.locale || 'en-IS', { month: 'long', year: 'numeric' });
          
          const isSelected = (year === currentYear && month === currentMonth);
          options.push(`<option value="${year}-${month}" ${isSelected ? 'selected' : ''}>${monthName}</option>`);
        }
        
        return options.join('');
      },
  
      // --- Styles (adds today highlight + disabled past) ---
      _style() {
        return `
          :host { --nt-radius:14px; --nt-pad:12px; --nt-font:system-ui,-apple-system,Segoe UI,Roboto,Arial; }
          * { box-sizing:border-box; }
          .nt-wrap { font-family:var(--nt-font); border:1px solid #e5e7eb; border-radius:var(--nt-radius); padding:var(--nt-pad);
                     max-width:860px; box-shadow:0 8px 24px rgba(0,0,0,.06); background:#fff; }
          .nt-top { display:grid; grid-template-rows:auto auto; gap:16px; }
          .nt-cal { border:1px solid #e5e7eb; border-radius:12px; overflow:hidden; position:relative; }
          .nt-cal-head { display:flex; align-items:center; justify-content:space-between; padding:10px 12px; background:#f9fafb; border-bottom:1px solid #e5e7eb; }
          .nt-title { font-weight:600; display:flex; align-items:center; }
          .nt-nav { display:flex; gap:6px; }
          .nt-btn { appearance:none; border:1px solid #e5e7eb; background:#fff; padding:6px 10px; border-radius:8px; cursor:pointer; }
          .nt-btn:hover { background:#f3f4f6; }
          .nt-btn[disabled] { opacity:0.5; cursor:not-allowed; }
          
          /* Month dropdown in header */
          .nt-month-dropdown { 
            appearance:auto; 
            border:1px solid #d1d5db; 
            background:#fff; 
            padding:6px 10px; 
            border-radius:6px; 
            cursor:pointer; 
            font-size:16px;
            font-weight:600;
            min-width:180px;
            padding-right:10px;
          }
          .nt-month-dropdown:hover { border-color:#3b82f6; background:#f8fafc; }
          .nt-month-dropdown:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 2px rgba(59,130,246,0.1); }
          
          /* Addon buttons */
          .nt-addon-buttons { display:flex; flex-direction:column; gap:8px; margin-top:8px; }
          .nt-addon-btn { 
            appearance:none; 
            border:2px solid #e5e7eb; 
            background:#fff; 
            padding:12px 16px; 
            border-radius:8px; 
            cursor:pointer; 
            text-align:left;
            transition:all 0.2s ease;
          }
          .nt-addon-btn:hover:not([disabled]) { 
            border-color:#3b82f6; 
            background:#f8fafc; 
          }
          .nt-addon-btn-selected { 
            border-color:#3b82f6; 
            background:#eff6ff; 
          }
          .nt-addon-btn-unavailable { 
            opacity:0.5; 
            cursor:not-allowed; 
          }
          .nt-addon-title { 
            font-weight:600; 
            font-size:14px; 
            margin-bottom:4px; 
          }
          .nt-addon-capacity { 
            font-size:12px; 
            color:#6b7280; 
          }
          .nt-availability-info {
            margin-top: 8px;
            padding: 4px 8px;
            background: #f3f4f6;
            border-radius: 4px;
            text-align: center;
          }
          .nt-availability-info small {
            color: #6b7280;
            font-size: 0.8em;
          }
          .nt-qty button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            background: #e5e7eb;
            color: #9ca3af;
          }
          .nt-loading { 
            text-align:center; 
            padding:12px; 
            color:#6b7280; 
            font-style:italic; 
          }
          .nt-grid { display:grid; grid-template-columns:repeat(7,1fr); }
          .nt-dow, .nt-day { padding:10px 8px; text-align:center; border-bottom:1px solid #f1f5f9; border-right:1px solid #f1f5f9; min-height:44px; }
          .nt-dow { font-size:12px; font-weight:600; background:#fcfcfd; }
          .nt-day:nth-child(7n), .nt-dow:nth-child(7n) { border-right:none; }
  
          .nt-day button { width:100%; border:1px solid transparent; background:transparent; padding:6px 0; border-radius:10px; cursor:pointer; }
          .nt-day button.nt-available { background:#ecfdf5; border-color:#10b981; color:#065f46; font-weight:600; }
          .nt-day button.nt-available:hover { background:#d1fae5; }
          .nt-day button.nt-available-low { background:#fef3c7; border-color:#f59e0b; color:#92400e; font-weight:600; }
          .nt-day button.nt-available-low:hover { background:#fde68a; }
          .nt-day button.nt-selected { outline:2px solid #10b981; }
          .nt-day button.nt-unavailable,
          .nt-day button.nt-past { background:#f3f4f6; color:#9ca3af; cursor:not-allowed; }
          /* Today ring (also on the selectable today of a same-day product) */
          .nt-day button.nt-today { position:relative; }
          .nt-day button.nt-today::after {
            content:"";
            position:absolute; inset:-2px;
            border:2px dashed #10b981; border-radius:12px;
            pointer-events:none;
          }
          .nt-day.nt-empty { background:#fafafa; }
  
          .nt-loading { position:absolute; inset:0; background:rgba(255,255,255,.6); display:flex; align-items:center; justify-content:center; font-size:12px; }
          .nt-spinner { width:16px; height:16px; border:2px solid #e5e7eb; border-top-color:#6b7280; border-radius:50%; animation:spin .8s linear infinite; margin-right:8px; }
          @keyframes spin { to { transform: rotate(360deg); } }
  
          .nt-side { border:1px solid #e5e7eb; border-radius:12px; padding:12px; display:grid; gap:12px; align-self:start; }
          .nt-section-title { font-weight:600; margin-bottom:4px; }
          .nt-row { display:flex; align-items:center; justify-content:space-between; }
          .nt-qty { display:inline-flex; align-items:center; gap:8px; border:1px solid #e5e7eb; border-radius:10px; padding:6px; }
          .nt-qty button { border:none; background:#f3f4f6; border-radius:8px; padding:4px 8px; cursor:pointer; }
          .nt-qty button:hover { background:#e5e7eb; }
          .nt-qty input { width:40px; text-align:center; border:none; background:transparent; font-weight:600; }
          .nt-total { display:flex; align-items:baseline; justify-content:space-between; font-size:18px; font-weight:700; padding-top:6px; border-top:1px dashed #e5e7eb; }
          .nt-book { display:flex; flex-direction:column; gap:8px; }
          .nt-book button { appearance:none; border:none; padding:16px 24px; border-radius:12px; background:#10b981; color:#fff; font-weight:700; font-size:16px; cursor:pointer; transition:all 0.2s ease; }
          .nt-book button:hover { filter:brightness(1.1); transform:translateY(-1px); }
          .nt-book button:disabled { background:#9ca3af; cursor:not-allowed; transform:none; }
          .nt-book-loading { display:flex; align-items:center; justify-content:center; gap:8px; }
          .nt-book-spinner { width:16px; height:16px; border:2px solid transparent; border-top-color:#fff; border-radius:50%; animation:spin 1s linear infinite; }
          .nt-note { font-size:12px; color:#6b7280; }
          .nt-sub { font-size:12px; color:#374151; }
          .nt-select { width:100%; padding:8px 12px; border:1px solid #e5e7eb; border-radius:8px; background:#fff; font-size:14px; }
          .nt-select:focus { outline:none; border-color:#10b981; box-shadow:0 0 0 3px rgba(16,185,129,0.1); }
          .nt-deposit-options { display:flex; flex-direction:column; gap:8px; }
          .nt-deposit-option { display:flex; align-items:center; padding:12px; border:2px solid #e5e7eb; border-radius:10px; cursor:pointer; transition:all 0.2s ease; background:#fff; }
          .nt-deposit-option:hover { border-color:#10b981; background:#f0fdf4; }
          .nt-deposit-option.selected { border-color:#10b981; background:#ecfdf5; }
          .nt-deposit-radio { margin:0; margin-right:12px; accent-color:#10b981; }
          .nt-deposit-content { flex:1; }
          .nt-deposit-title { font-weight:600; color:#111827; margin-bottom:2px; }
          .nt-deposit-desc { font-size:12px; color:#6b7280; }
          
          /* Extras styles */
          .nt-extras-section { margin-top:12px; }
          .nt-extras-field { margin-bottom:16px; }
          .nt-extras-label { display:block; font-weight:600; margin-bottom:8px; color:#111827; }
          .nt-extras-checkbox-group { display:flex; flex-direction:column; gap:8px; }
          .nt-extras-checkbox { display:flex; align-items:center; padding:8px 12px; border:1px solid #e5e7eb; border-radius:8px; cursor:pointer; transition:all 0.2s ease; background:#fff; }
          .nt-extras-checkbox:hover { border-color:#10b981; background:#f0fdf4; }
          .nt-extras-checkbox input[type="checkbox"] { margin:0; margin-right:8px; accent-color:#10b981; }
          .nt-extras-checkbox-label { flex:1; font-size:14px; }
          .nt-extras-price { font-weight:600; color:#10b981; font-size:14px; }
          .nt-extras-select { width:100%; padding:8px 12px; border:1px solid #e5e7eb; border-radius:8px; background:#fff; font-size:14px; }
          .nt-extras-select:focus { outline:none; border-color:#10b981; box-shadow:0 0 0 3px rgba(16,185,129,0.1); }
          .nt-extras-textarea { width:100%; padding:8px 12px; border:1px solid #e5e7eb; border-radius:8px; background:#fff; font-size:14px; font-family:inherit; min-height:80px; resize:vertical; }
          .nt-extras-textarea:focus { outline:none; border-color:#10b981; box-shadow:0 0 0 3px rgba(16,185,129,0.1); }
          .nt-extras-textarea:invalid { border-color:#ef4444; }
          .nt-extras-content { padding:8px 12px; background:#f9fafb; border-radius:8px; font-size:14px; color:#374151; }
          
          /* Extras occupancy styles */
          .nt-extras-option { margin-bottom:12px; }
          .nt-extras-occupancy { margin-top:12px; padding:12px; background:#f9fafb; border-radius:8px; border:1px solid #e5e7eb; }
          .nt-extras-occupancy-title { font-size:12px; font-weight:600; color:#374151; margin-bottom:8px; }
          .nt-extras-occupancy-grid { display:flex; flex-direction:column; gap:8px; }
          .nt-extras-occupancy-item { display:flex; align-items:center; justify-content:space-between; padding:8px; background:#fff; border-radius:6px; border:1px solid #e5e7eb; }
          .nt-extras-occupancy-label { font-size:13px; color:#374151; flex:1; }
          .nt-extras-occupancy-subtotal { font-size:13px; font-weight:600; color:#10b981; min-width:60px; text-align:right; }
          
          /* Shoe size selection styles */
          .nt-extras-shoe-size { margin-top:12px; padding:12px; background:#f9fafb; border-radius:8px; border:1px solid #e5e7eb; }
          .nt-extras-shoe-size-grid { display:flex; flex-direction:column; gap:16px; }
          .nt-extras-shoe-size-person { background:#fff; border-radius:8px; padding:12px; border:1px solid #e5e7eb; }
          .nt-extras-shoe-size-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; padding-bottom:8px; border-bottom:1px solid #e5e7eb; }
          .nt-extras-shoe-size-selections { display:flex; flex-direction:column; gap:8px; }
          .nt-extras-shoe-size-item { display:flex; align-items:center; gap:8px; }
          .nt-extras-shoe-size-label { font-size:13px; color:#374151; min-width:100px; }
          .nt-extras-shoe-size-select { padding:6px 8px; border:1px solid #e5e7eb; border-radius:6px; background:#fff; font-size:13px; min-width:120px; }
          .nt-extras-shoe-size-select:focus { outline:none; border-color:#10b981; box-shadow:0 0 0 2px rgba(16,185,129,0.1); }
          .nt-extras-shoe-size-select:disabled { background:#f3f4f6; color:#9ca3af; cursor:not-allowed; }
        `;
      },
  
      // --- Availability (no cache; always fetch) ---

      /**
       * GET `${apiBase}${path}`; on a network error or a 5xx, retry the same
       * path once on apiFallbackBase (Render move Phase 4: the old origin,
       * kept fresh by its own Bókun webhook while the new widget build rolls
       * out through CloudFront). A 4xx is the same on both and not retried.
       */
      async _fetchAvailabilityApi(path) {
        const cfg = this._state.config;
        const init = { headers: cfg.apiHeaders || {} };
        const fallback = cfg.apiFallbackBase;
        const canFallBack = !!fallback && fallback !== cfg.apiBase;
        try {
          const resp = await fetch(`${cfg.apiBase}${path}`, init);
          if (resp.status < 500 || !canFallBack) return resp;
          console.warn(`[NiceCalendar] ${cfg.apiBase} answered ${resp.status} — retrying on ${fallback}`);
        } catch (err) {
          if (!canFallBack) throw err;
          console.warn(`[NiceCalendar] ${cfg.apiBase} unreachable — retrying on ${fallback}`, err);
        }
        return fetch(`${fallback}${path}`, init);
      },

      async _fetchAvailability(y, m0) {
        const cfg = this._state.config;
        const month1 = m0 + 1;
        this._state.loading = true;
        this._rerender();
        try {
          // Fetch main product availability
          let path = `/availability?product_id=${encodeURIComponent(cfg.productId)}&year=${y}&month=${month1}`;
          // On initial load only, ask backend to auto-jump to first available month
          if (!this._state.initialAutoSearchDone) {
            path += '&auto_jump=1';
            this._state.initialAutoSearchDone = true;
          }
          const resp = await this._fetchAvailabilityApi(path);
          if (!resp.ok) throw new Error(`Availability fetch failed: ${resp.status}`);
          const data = await resp.json();
  
          // If backend auto-jumped to a different month, use that month consistently
          // for both UI state and addon availability fetches.
          let effectiveYear = y;
          let effectiveMonth1 = month1;
          if (
            typeof data.year === 'number' &&
            typeof data.month === 'number' &&
            data.month >= 1 &&
            data.month <= 12
          ) {
            effectiveYear = data.year;
            effectiveMonth1 = data.month;
            this._state.viewYear = effectiveYear;
            this._state.viewMonth = effectiveMonth1 - 1; // backend months are 1-based
          }

          // Store timeId and bokunId from the response if available
          if (data.timeId != null) this._state.config.timeId = data.timeId;
          if (data.bokunId != null) this._state.config.bokunId = data.bokunId;
          // Store skip_pickup from the response (only explicit true hides pickup)
          this._state.config.skip_pickup = data.skip_pickup === true;
          // Third-party pickup list served with the availability (spec §3);
          // null = keep the inline / catalog list as before.
          this._state.config.pickup_places = Array.isArray(data.pickup_places) ? data.pickup_places : null;
          // Spec 2026-09-11 §4: deposit offer + required pickup ride on the
          // availability response; an older backend behaves as today.
          this._state.config.deposit = this._depositRuleFrom(data.deposit);
          this._state.config.pickup_required = data.pickup_required === true;
          // Dropoff list + requiredness ride on the availability response
          // (spec 2026-09-17 §3); an older backend behaves as today.
          this._state.config.dropoff_places = Array.isArray(data.dropoff_places) ? data.dropoff_places : null;
          this._state.config.dropoff_required = data.dropoff_required === true;
          // Spec 2026-09-17 §4: only an explicit true opens today's cell;
          // an older backend (no field) behaves exactly as today.
          this._state.config.same_day_booking = data.same_day_booking === true;
          // Spec 2026-10-08 B3: which cart BOOK NOW posts to. Only an explicit
          // 'commerce' leaves WooCommerce; an older backend behaves as today.
          // Addon responses never reach this block (main product decides).
          this._state.config.cart_target = data.cart_target === 'commerce' ? 'commerce' : 'woocommerce';
          this._state.config.cart_url = typeof data.cart_url === 'string' ? data.cart_url : null;
          // Store multiday_tours_time_ids from the response if available
          if (data.multiday_tours_time_ids != null) {
            this._state.config.multiday_tours_time_ids = data.multiday_tours_time_ids;
          }
  
          const dayAvail = new Map();
          const dayCaps = new Map();
          const dayStartTimes = new Map();
          if (Array.isArray(data.days)) {
            for (const d of data.days) {
              if (!d?.date) continue;
              dayAvail.set(d.date, !!d.available);
              if (typeof d.capacity === 'number') dayCaps.set(d.date, d.capacity);
              // Store start_times if present
              if (d.start_times && Array.isArray(d.start_times) && d.start_times.length > 0) {
                dayStartTimes.set(d.date, d.start_times);
              }
            }
          }
  
          // Fetch availability for non-duplicate addons and combine results
          if (cfg.productAddons && cfg.productAddons.length > 0) {
            const nonDuplicateAddons = cfg.productAddons.filter(addon => addon.addon_product_id != cfg.productId);
            
            if (nonDuplicateAddons.length > 0) {
              console.log('[NiceCalendar] Fetching availability for', nonDuplicateAddons.length, 'non-duplicate addons');
              
              const addonPromises = nonDuplicateAddons.map(async (addon) => {
                try {
                  const addonResp = await this._fetchAvailabilityApi(
                    `/availability?product_id=${encodeURIComponent(addon.addon_product_id)}&year=${effectiveYear}&month=${effectiveMonth1}`,
                  );
                  if (!addonResp.ok) throw new Error(`Addon availability fetch failed: ${addonResp.status}`);
                  const addonData = await addonResp.json();
                  return addonData;
                } catch (error) {
                  console.error(`Error fetching availability for addon ${addon.addon_product_id}:`, error);
                  return { days: [] };
                }
              });
  
              const addonResults = await Promise.all(addonPromises);
              
              // Store addon availability data for reuse during day selection
              this._state.monthAddonData = new Map();
              
              // Combine availability - a day is available if ANY product (main or addon) is available
              // But keep capacity separate for each product
              addonResults.forEach((addonData, index) => {
                const addon = nonDuplicateAddons[index];
                
                // Store addon data for later use
                this._state.monthAddonData.set(addon.addon_product_id, addonData);
                
                if (Array.isArray(addonData.days)) {
                  for (const d of addonData.days) {
                    if (!d?.date) continue;
                    
                    // If this addon is available, mark the day as available (combine availability)
                    if (d.available) {
                      dayAvail.set(d.date, true);
                    }
                    // Note: We don't modify dayCaps here to preserve main product capacity
                    // Addon capacity is handled separately in _fetchAddonAvailability()
                    // start_times are stored in monthAddonData and will be accessed via _fetchAddonAvailability()
                  }
                }
              });
            }
          }
  
          this._state.dayAvailMap = dayAvail;
          this._state.dayCapacityMap = dayCaps;
          this._state.dayStartTimesMap = dayStartTimes;
        } catch (e) {
          console.error('[NiceCalendar] availability fetch error', e);
          // On error, mark all as unavailable
          const map = new Map(), caps = new Map();
          const n = this._daysInMonth(y, m0);
          for (let d = 1; d <= n; d++) map.set(this._ymd(y, m0, d), false);
          this._state.dayAvailMap = map;
          this._state.dayCapacityMap = caps;
        } finally {
          this._state.loading = false;
          this._rerender();
        }
      },
  
  
      // Get current person fields (from main product or selected addon)
      _getCurrentPersonFields() {
        if (this._state.selectedAddon) {
          // Return person types from selected addon
          return this._state.selectedAddon.person_types || [];
        }
        // Return main product person fields
        return this._state.config.personFields || [];
      },

      // Get person fields sorted for display (Adults, Teenagers, Children)
      _getPersonFieldsInDisplayOrder() {
        const fields = this._getCurrentPersonFields();
        return [...fields].sort((a, b) => {
          const orderA = PERSON_TYPE_DISPLAY_ORDER[a.viPersonType] ?? 99;
          const orderB = PERSON_TYPE_DISPLAY_ORDER[b.viPersonType] ?? 99;
          return orderA - orderB;
        });
      },

      // Get total number of people currently selected
      _getTotalPersonCount() {
        const personFields = this._getCurrentPersonFields();
        let total = 0;
        personFields.forEach(field => {
          total += this._state.personCounts[field.fieldId] || 0;
        });
        return total;
      },
  
      // Get available capacity for the selected date
      _getAvailableCapacity() {
        if (!this._state.selectedDate) return 0;
        
        const s = this._state, cfg = s.config;
        let availableCapacity = 0;
        
        if (s.selectedAddon) {
          // Use addon's capacity if addon is selected
          const addonAvailability = s.addonAvailability.get(s.selectedAddon.addon_product_id);
          availableCapacity = addonAvailability?.capacity || 0;
        } else {
          // Use main product capacity
          const cap = this._capacityFor(s.selectedDate.getFullYear(), s.selectedDate.getMonth(), s.selectedDate.getDate());
          availableCapacity = (cap == null) ? (cfg.availabilityCount ?? 999) : cap;
        }
        
        return availableCapacity;
      },
      
      // Format capacity for display: show "20+" when > 20
      _formatCapacityDisplay(n) {
        if (typeof n !== 'number' || n < 0) return String(n ?? 0);
        return n > 20 ? '20+' : String(n);
      },
      
      // Check if all required textarea fields are filled
      _areRequiredTextareasFilled() {
        if (!this._state.config.extras) return true;
        
        // Check if extras config contains error messages
        if (typeof this._state.config.extras === 'string' && 
            this._state.config.extras.includes('No WCPA fields found for this product')) {
          return true;
        }
        
        if (typeof this._state.config.extras === 'object' && 
            ((this._state.config.extras.message && 
              this._state.config.extras.message.includes('No WCPA fields found for this product')) ||
             (this._state.config.extras.error && 
              this._state.config.extras.error.includes('WCPA plugin not active')))) {
          return true;
        }
        
        // Check all field sets for required textareas
        for (const fieldSet of this._state.config.extras.fields_sets || []) {
          if (!fieldSet.fields || fieldSet.fields.length === 0) continue;
          
          for (const field of fieldSet.fields) {
            if (field.type === 'textarea' && field.required) {
              const fieldValue = this._state.extrasValues.get(field.fieldId) || '';
              const trimmedValue = fieldValue.trim();
              
              if (!trimmedValue) {
                return false; // Required textarea is empty
              }
            }
          }
        }
        
        return true; // All required textareas are filled
      },
  
      // Evaluate conditional logic for extras fields
      _evaluateConditionalLogic(field, conditionalLogic) {
        if (!conditionalLogic || !conditionalLogic.enabled) return true;
  
        const relations = conditionalLogic.relations || [];
        let result = true;
  
        for (const relation of relations) {
          const operator = relation.operator;
          const rules = relation.rules;
  
          // Handle operator - convert to lowercase if it's a string, otherwise treat as boolean
          let operatorStr = '';
          if (typeof operator === 'string') {
            operatorStr = operator.toLowerCase();
          } else if (typeof operator === 'boolean') {
            // If operator is boolean, treat false as 'and' and true as 'or' (or vice versa based on context)
            operatorStr = operator ? 'or' : 'and';
          }
  
          if (Array.isArray(rules)) {
            // Multiple rules with operator
            let ruleResult = true;
            for (const rule of rules) {
              const ruleValue = this._evaluateRule(rule);
              if (operatorStr === 'and') {
                ruleResult = ruleResult && ruleValue;
              } else if (operatorStr === 'or') {
                ruleResult = ruleResult || ruleValue;
              }
            }
            result = result && ruleResult;
          } else if (rules) {
            // Single rule
            const ruleValue = this._evaluateRule(rules);
            if (operatorStr === 'and') {
              result = result && ruleValue;
            } else if (operatorStr === 'or') {
              result = result || ruleValue;
            }
          }
        }
  
        return conditionalLogic.rule === 'show' ? result : !result;
      },
  
      // Evaluate a single rule
      _evaluateRule(rule) {
        if (!rule || !rule.cl_field) return true;
  
        const fieldValue = this._state.extrasValues.get(rule.cl_field) || '';
        const expectedValue = rule.cl_val;
        const relation = rule.cl_relation;
  
        switch (relation) {
          case 'is':
            return fieldValue === expectedValue;
          case 'is_not':
            return fieldValue !== expectedValue;
          case 'is_empty':
            return !fieldValue || fieldValue === '';
          case 'is_not_empty':
            return fieldValue && fieldValue !== '';
          case 'is_greater':
            return parseInt(fieldValue) > parseInt(expectedValue);
          case 'is_less':
            return parseInt(fieldValue) < parseInt(expectedValue);
          default:
            return true;
        }
      },
  
      // Update extras field visibility based on conditional logic
      _updateExtrasVisibility() {
        if (!this._state.config.extras) return;
  
        // Check if extras config contains "No WCPA fields found" message
        if (typeof this._state.config.extras === 'string' && 
            this._state.config.extras.includes('No WCPA fields found for this product')) {
          return;
        }
  
        // Check if extras config is an object but has the error message
        if (typeof this._state.config.extras === 'object' && 
            this._state.config.extras.message && 
            this._state.config.extras.message.includes('No WCPA fields found for this product')) {
          return;
        }
        
        // Check for "WCPA plugin not active" error
        if (typeof this._state.config.extras === 'object' && 
            this._state.config.extras.error && 
            this._state.config.extras.error.includes('WCPA plugin not active')) {
          return;
        }
  
        this._state.config.extras.fields_sets.forEach(fieldSet => {
          // Skip empty field sets
          if (!fieldSet.fields || fieldSet.fields.length === 0) {
            return;
          }
          
          fieldSet.fields.forEach(field => {
            if (field.conditional_logic) {
              const isVisible = this._evaluateConditionalLogic(field, field.conditional_logic);
              this._state.extrasVisibility.set(field.fieldId, isVisible);
            } else {
              this._state.extrasVisibility.set(field.fieldId, true);
            }
          });
        });
      },
  
      // Render extras section
      _renderExtras() {
        if (!this._state.config.extras) return '';

        // Check if extras config contains "No WCPA fields found" message
        if (typeof this._state.config.extras === 'string' && 
            this._state.config.extras.includes('No WCPA fields found for this product')) {
          console.log('[NiceCalendar] Skipping extras rendering - no WCPA fields found');
          return '';
        }

        // Check if extras config is an object but has the error message
        if (typeof this._state.config.extras === 'object' && 
            this._state.config.extras.message && 
            this._state.config.extras.message.includes('No WCPA fields found for this product')) {
          console.log('[NiceCalendar] Skipping extras rendering - no WCPA fields found');
          return '';
        }
        
        // Check for "WCPA plugin not active" error
        if (typeof this._state.config.extras === 'object' && 
            this._state.config.extras.error && 
            this._state.config.extras.error.includes('WCPA plugin not active')) {
          console.log('[NiceCalendar] Skipping extras rendering - WCPA plugin not active');
          return '';
        }
  
        // Update visibility before rendering
        this._updateExtrasVisibility();
  
        return `
          <div class="nt-extras-section">
            <div class="nt-section-title">Extras</div>
            ${this._state.config.extras.fields_sets
              .filter(fieldSet => fieldSet.fields && fieldSet.fields.length > 0)
              .map(fieldSet => `
              <div class="nt-field-set">
                ${fieldSet.fields.map(field => this._renderExtrasField(field)).join('')}
              </div>
            `).join('')}
          </div>
        `;
      },
  
      // Render individual extras field
      _renderExtrasField(field) {
        const isVisible = this._state.extrasVisibility.get(field.fieldId);
        if (!isVisible) return '';
  
        const currentValue = this._state.extrasValues.get(field.fieldId) || field.value || '';
  
        switch (field.type) {
          case 'content':
            return '';
          
          case 'checkbox-group':
            return `
              <div class="nt-extras-field">
                <label class="nt-extras-label">${field.label}</label>
                <div class="nt-extras-checkbox-group">
                  ${field.options.map(option => {
                    const isSelected = currentValue.includes(option.value);
                    return `
                      <div class="nt-extras-option">
                        <label class="nt-extras-checkbox">
                          <input type="checkbox" 
                                 data-field-id="${field.fieldId}" 
                                 data-option-value="${option.value}"
                                 ${isSelected ? 'checked' : ''}>
                          <span class="nt-extras-checkbox-label">${option.label}</span>
                          ${option.price ? `<span class="nt-extras-price">+${this._fmtCurrency(parseInt(option.price) || 0)}</span>` : ''}
                        </label>
                        ${isSelected ? this._renderExtrasOccupancy(field.fieldId, option.value, option.price) : ''}
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          
          case 'select':
            // Skip select fields that are used for pricing (they have price but no meaningful options)
            // These are typically the Adults/Teenager/Children select fields that just provide pricing info
            if (field.price && field.options && field.options.length > 0 && 
                field.options.every(opt => !isNaN(parseInt(opt.value)))) {
              // This is a pricing select field (like Adults 1-10), don't render it
              return '';
            }
            
            // Skip shoe size select fields (they are handled in the shoe size selection interface)
            if (field.label && field.label.toLowerCase().includes('size') && field.label.includes('#')) {
              // This is a shoe size select field, don't render it as a regular select
              return '';
            }
            
            return `
              <div class="nt-extras-field">
                <label class="nt-extras-label">${field.label}</label>
                <select class="nt-extras-select" data-field-id="${field.fieldId}">
                  <option value="">Select ${field.label}</option>
                  ${field.options.map(option => `
                    <option value="${option.value}" ${currentValue === option.value ? 'selected' : ''}>
                      ${option.label}${option.price ? ` (+${this._fmtCurrency(parseInt(option.price) || 0)})` : ''}
                    </option>
                  `).join('')}
                </select>
              </div>
            `;
          
          case 'textarea':
            return `
              <div class="nt-extras-field">
                <label class="nt-extras-label">
                  ${field.label}
                  ${field.required ? '<span style="color:#ef4444;">*</span>' : ''}
                </label>
                <textarea 
                  class="nt-extras-textarea" 
                  data-field-id="${field.fieldId}"
                  ${field.required ? 'required' : ''}
                  placeholder="${field.label}"
                >${currentValue}</textarea>
              </div>
            `;
          
          case 'hidden':
            // Hidden fields are handled in form submission, not rendered
            return '';
          
          default:
            return '';
        }
      },
  
      // Get available person types from an extras field set
      _getAvailablePersonTypesFromFieldSet(fieldSet) {
        if (!fieldSet || !fieldSet.fields) {
          return [];
        }
        
        // Define all possible person types (order: Adults, Teenagers, Children)
        const allPersonTypes = [
          { viPersonType: 'adults', label: 'Adults', searchLabels: ['adult', 'adults'] },
          { viPersonType: 'teenager', label: 'Teenagers', searchLabels: ['teenager', 'teenagers'] },
          { viPersonType: 'children', label: 'Children', searchLabels: ['child', 'children'] }
        ];
        
        // Filter to only include person types that have a corresponding quantity select field in the field set
        // Quantity fields are select fields with a price (not size fields which don't have price)
        return allPersonTypes.filter(personType => {
          return fieldSet.fields.some(field => {
            if (field.type !== 'select' || !field.label || !field.price) {
              return false;
            }
            // Skip size fields (they contain "Size" and "#" in the label)
            if (field.label.toLowerCase().includes('size') && field.label.includes('#')) {
              return false;
            }
            const labelLower = field.label.toLowerCase();
            return personType.searchLabels.some(searchLabel => 
              labelLower.includes(searchLabel)
            );
          });
        });
      },
  
      // Render occupancy inputs for extras
      _renderExtrasOccupancy(fieldId, optionValue, basePrice) {
        const personFields = this._getCurrentPersonFields();
        const occupancyKey = `${fieldId}_${optionValue}`;
        
        // Initialize occupancy if not exists
        if (!this._state.extrasOccupancy.has(occupancyKey)) {
          this._state.extrasOccupancy.set(occupancyKey, new Map());
        }
        
        const occupancy = this._state.extrasOccupancy.get(occupancyKey);
        
        // Find the field set that contains this checkbox field
        let currentFieldSet = null;
        if (this._state.config.extras) {
          this._state.config.extras.fields_sets.forEach((fieldSet, index) => {
            if (fieldSet.fields) {
              const checkboxField = fieldSet.fields.find(field => field.fieldId === fieldId);
              if (checkboxField) {
                currentFieldSet = fieldSet;
                console.log('[Debug] Found field set', index, 'for fieldId', fieldId, ':', {
                  fieldSetIndex: index,
                  fieldId: fieldId,
                  checkboxFieldName: checkboxField.name,
                  totalFields: fieldSet.fields.length,
                  sizeFields: fieldSet.fields.filter(f => 
                    f.type === 'select' && 
                    f.label && 
                    f.label.toLowerCase().includes('size')
                  ).map(f => f.label)
                });
              }
            }
          });
        }
        
        // Check if this is a shoe size type extra (has size selection fields)
        const hasSizeSelection = currentFieldSet && currentFieldSet.fields && 
          currentFieldSet.fields.some(field => 
            field.type === 'select' && 
            field.label && 
            field.label.toLowerCase().includes('size') &&
            field.label.includes('#')
          );
        
        // Debug logging for field set detection
        console.log('[Debug] Field set detection for', fieldId, ':', {
          hasFieldSet: !!currentFieldSet,
          hasFields: currentFieldSet?.fields?.length || 0,
          hasSizeSelection: hasSizeSelection,
          sizeFields: currentFieldSet?.fields?.filter(f => 
            f.type === 'select' && 
            f.label && 
            f.label.toLowerCase().includes('size') &&
            f.label.includes('#')
          ).map(f => f.label) || []
        });
        
        if (hasSizeSelection) {
          // Render shoe size selection interface
          return this._renderShoeSizeSelection(fieldId, optionValue, currentFieldSet, occupancy);
        } else {
          // Render regular occupancy interface
          // Only show person types that are available in the extras field set
          const allPersonTypes = currentFieldSet 
            ? this._getAvailablePersonTypesFromFieldSet(currentFieldSet)
            : [
                { viPersonType: 'adults', label: 'Adults' },
                { viPersonType: 'teenager', label: 'Teenagers' },
                { viPersonType: 'children', label: 'Children' }
              ];
          
          return `
            <div class="nt-extras-occupancy">
              <div class="nt-extras-occupancy-title">Select quantity for each person type:</div>
              <div class="nt-extras-occupancy-grid">
                ${allPersonTypes.map(personType => {
                  const currentCount = occupancy.get(personType.viPersonType) || 0;
                  
                  // Find the corresponding person field in the current product
                  const personField = personFields.find(pf => pf.viPersonType === personType.viPersonType);
                  let maxCount = 0;
                  
                  if (personField) {
                    // Use the count from the current product's person fields
                    maxCount = this._state.personCounts[personField.fieldId] || 0;
                  } else {
                    // If person type not found in current product, use total capacity as fallback
                    maxCount = this._getAvailableCapacity();
                  }
                  
                  // Get the price for this specific person type from the current field set
                  let personPrice = 0;
                  if (currentFieldSet && currentFieldSet.fields) {
                    const selectField = currentFieldSet.fields.find(field => 
                      field.type === 'select' && 
                      field.label && 
                      field.label.toLowerCase().includes(personType.viPersonType.toLowerCase()) &&
                      field.price
                    );
                    if (selectField) {
                      personPrice = parseInt(selectField.price) || 0;
                    }
                  }
                  
                  const buttonDataQty = `${occupancyKey}_${personType.viPersonType}`;
                  
                  return `
                    <div class="nt-extras-occupancy-item">
                      <label class="nt-extras-occupancy-label">
                        ${personType.label} (${this._fmtCurrency(personPrice)} each)
                      </label>
                      <div class="nt-qty">
                        <button data-qty="${buttonDataQty}-">-</button>
                        <input type="text" value="${currentCount}" readonly />
                        <button data-qty="${buttonDataQty}+">+</button>
                      </div>
                      <div class="nt-extras-occupancy-subtotal">
                        ${this._fmtCurrency(currentCount * personPrice)}
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }
      },
  
      // Render shoe size selection interface for hiking boots type extras
      _renderShoeSizeSelection(fieldId, optionValue, fieldSet, occupancy) {
        const personFields = this._getCurrentPersonFields();
        const occupancyKey = `${fieldId}_${optionValue}`;
        
        // Only show person types that are available in the extras field set
        const allPersonTypes = fieldSet 
          ? this._getAvailablePersonTypesFromFieldSet(fieldSet)
          : [
              { viPersonType: 'adults', label: 'Adults' },
              { viPersonType: 'teenager', label: 'Teenagers' },
              { viPersonType: 'children', label: 'Children' }
            ];
        
        // Get pricing information
        const pricingFields = {};
        allPersonTypes.forEach(personType => {
          const selectField = fieldSet.fields.find(field => 
            field.type === 'select' && 
            field.label && 
            field.label.toLowerCase().includes(personType.viPersonType.toLowerCase()) &&
            field.price
          );
          if (selectField) {
            pricingFields[personType.viPersonType] = parseInt(selectField.price) || 0;
          }
        });
        
        return `
          <div class="nt-extras-shoe-size">
            <div class="nt-extras-occupancy-title">Select quantity and shoe sizes:</div>
            <div class="nt-extras-shoe-size-grid">
              ${allPersonTypes.map(personType => {
                const currentCount = occupancy.get(personType.viPersonType) || 0;
                
                // Find the corresponding person field in the current product
                const personField = personFields.find(pf => pf.viPersonType === personType.viPersonType);
                let maxCount = 0;
                
                if (personField) {
                  // Use the count from the current product's person fields
                  maxCount = this._state.personCounts[personField.fieldId] || 0;
                } else {
                  // If person type not found in current product, use total capacity as fallback
                  maxCount = this._getAvailableCapacity();
                }
                
                const personPrice = pricingFields[personType.viPersonType] || 0;
                const buttonDataQty = `${occupancyKey}_${personType.viPersonType}`;
                
                // Get size fields for this person type - be more specific with the matching
                const personTypeLabel = personType.viPersonType === 'adults' ? 'Adult' : 
                                       personType.viPersonType === 'teenager' ? 'Teenager' : 
                                       personType.viPersonType === 'children' ? 'Children' : '';
                
                const personSizeFields = fieldSet.fields.filter(field => 
                  field.type === 'select' && 
                  field.label && 
                  (
                    // Match both singular and plural for children
                    (personType.viPersonType === 'children' && (field.label.includes('Size Children #') || field.label.includes('Size Child #')))
                    ||
                    // Default matching for adults/teenager
                    (personType.viPersonType !== 'children' && field.label.includes(`Size ${personTypeLabel} #`))
                  )
                );
                
                // Debug logging for children size fields
                if (personType.viPersonType === 'children') {
                  console.log('[Debug] Children size field detection:', {
                    personTypeLabel: personTypeLabel,
                    searchPattern: `Size ${personTypeLabel} #`,
                    allFields: fieldSet.fields.map(f => ({ type: f.type, label: f.label })),
                    sizeFields: personSizeFields.map(f => ({ fieldId: f.fieldId, label: f.label }))
                  });
                }
                
                return `
                  <div class="nt-extras-shoe-size-person">
                    <div class="nt-extras-shoe-size-header">
                      <label class="nt-extras-occupancy-label">
                        ${personType.label} (${this._fmtCurrency(personPrice)} each)
                      </label>
                      <div class="nt-qty">
                        <button data-qty="${buttonDataQty}-">-</button>
                        <input type="text" value="${currentCount}" readonly />
                        <button data-qty="${buttonDataQty}+">+</button>
                      </div>
                      <div class="nt-extras-occupancy-subtotal">
                        ${this._fmtCurrency(currentCount * personPrice)}
                      </div>
                    </div>
                    <div class="nt-extras-shoe-size-selections">
                      ${currentCount > 0 ? Array.from({length: currentCount}, (_, index) => {
                        const sizeField = personSizeFields[index];
                        if (!sizeField) return '';
                        
                        // Use the field ID directly as the key for easier lookup
                        const sizeKey = sizeField.fieldId;
                        const currentSize = this._state.extrasShoeSizes?.get(sizeKey) || '';
                        
                        
                        return `
                          <div class="nt-extras-shoe-size-item">
                            <label class="nt-extras-shoe-size-label">
                              ${sizeField.label}:
                            </label>
                            <select class="nt-extras-shoe-size-select" data-size-key="${sizeKey}">
                              <option value="">Select size</option>
                              ${sizeField.options.map(option => `
                                <option value="${option.value}" ${currentSize === option.value ? 'selected' : ''}>
                                  ${option.label}
                                </option>
                              `).join('')}
                            </select>
                          </div>
                        `;
                      }).join('') : ''}
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      },
  
      // Calculate total for all extras
      _calculateExtrasTotal() {
        if (!this._state.config.extras) return 0;

        // Check if extras config contains "No WCPA fields found" message
        if (typeof this._state.config.extras === 'string' && 
            this._state.config.extras.includes('No WCPA fields found for this product')) {
          return 0;
        }

        // Check if extras config is an object but has the error message
        if (typeof this._state.config.extras === 'object' && 
            this._state.config.extras.message && 
            this._state.config.extras.message.includes('No WCPA fields found for this product')) {
          return 0;
        }
        
        // Check for "WCPA plugin not active" error
        if (typeof this._state.config.extras === 'object' && 
            this._state.config.extras.error && 
            this._state.config.extras.error.includes('WCPA plugin not active')) {
          return 0;
        }
        
        let extrasTotal = 0;
        
        this._state.config.extras.fields_sets.forEach(fieldSet => {
          // Skip empty field sets
          if (!fieldSet.fields || fieldSet.fields.length === 0) {
            return;
          }
          
          fieldSet.fields.forEach(field => {
            if (field.type === 'checkbox-group') {
              const selectedValues = this._state.extrasValues.get(field.fieldId) || [];
              
              selectedValues.forEach(optionValue => {
                const occupancyKey = `${field.fieldId}_${optionValue}`;
                const occupancy = this._state.extrasOccupancy.get(occupancyKey);
                
                if (occupancy) {
                  occupancy.forEach((count, personType) => {
                    // Get the price for this specific person type from the current field set
                    let personPrice = 0;
                    if (fieldSet.fields) {
                      const selectField = fieldSet.fields.find(f => 
                        f.type === 'select' && 
                        f.label && 
                        f.label.toLowerCase().includes(personType.toLowerCase()) &&
                        f.price
                      );
                      if (selectField) {
                        personPrice = parseInt(selectField.price) || 0;
                      }
                    }
                    
                    extrasTotal += count * personPrice;
                  });
                }
              });
            }
          });
        });
        
        return extrasTotal;
      },
  
      // Fetch availability for all product addons for a specific date
      async _fetchAddonAvailability(date) {
        console.log('[NiceCalendar] _fetchAddonAvailability called with date:', date);
        console.log('[NiceCalendar] productAddons:', this._state.config.productAddons);
        
        if (!this._state.config.productAddons || this._state.config.productAddons.length === 0) {
          console.log('[NiceCalendar] No product addons found, skipping fetch');
          return;
        }
  
        console.log('[NiceCalendar] Starting addon availability fetch for', this._state.config.productAddons.length, 'addons');
        this._state.addonLoading = true;
        this._rerender();
  
        try {
          const year = date.getFullYear();
          const month = date.getMonth() + 1; // JavaScript months are 0-based
          const day = date.getDate();
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  
          // Process availability for each addon using stored data
          const addonResults = this._state.config.productAddons.map((addon) => {
            // If addon matches main product ID, reuse main product availability
            if (addon.addon_product_id == this._state.config.productId) {
              console.log('[NiceCalendar] Addon matches main product, reusing main product availability');
              const mainProductAvailability = this._state.dayAvailMap.get(dateStr);
              const mainProductCapacity = this._state.dayCapacityMap.get(dateStr);
              const mainProductStartTimes = this._state.dayStartTimesMap.get(dateStr);
              
              return {
                addon_id: addon.addon_product_id,
                available: mainProductAvailability || false,
                capacity: mainProductCapacity || 0,
                bokun_id: this._state.config.bokunId,
                time_id: this._state.config.timeId,
                multiday_tours_time_ids: this._state.config.multiday_tours_time_ids,
                skip_pickup: this._state.config.skip_pickup,
                pickup_places: this._state.config.pickup_places ?? null,
                dropoff_places: this._state.config.dropoff_places ?? null,
                // Left unset on purpose: every branch falls back to the
                // product's own rule at read time (`entry?.x ?? s.config?.x`).
                deposit: undefined,
                pickup_required: undefined,
                dropoff_required: undefined,
                start_times: mainProductStartTimes || null
              };
            }
            
            // For non-duplicate addons, reuse stored month data
            const storedAddonData = this._state.monthAddonData?.get(addon.addon_product_id);
            if (storedAddonData) {
              console.log('[NiceCalendar] Reusing stored addon data for day selection');
              
              // Find availability for the specific date
              const dayData = storedAddonData.days?.find(d => d.date === dateStr);
              
              return {
                addon_id: addon.addon_product_id,
                available: dayData?.available || false,
                capacity: dayData?.capacity || 0,
                bokun_id: storedAddonData.bokunId,
                time_id: storedAddonData.timeId,
                multiday_tours_time_ids: storedAddonData.multiday_tours_time_ids,
                skip_pickup: storedAddonData.skip_pickup === true,
                pickup_places: Array.isArray(storedAddonData.pickup_places) ? storedAddonData.pickup_places : null,
                dropoff_places: Array.isArray(storedAddonData.dropoff_places) ? storedAddonData.dropoff_places : null,
                deposit: storedAddonData.deposit,
                pickup_required: storedAddonData.pickup_required,
                dropoff_required: storedAddonData.dropoff_required,
                start_times: dayData?.start_times || null
              };
            }
            
            // Fallback if no stored data (shouldn't happen in normal flow)
            console.warn('[NiceCalendar] No stored data for addon, marking as unavailable');
            return {
              addon_id: addon.addon_product_id,
              available: false,
              capacity: 0,
              bokun_id: null,
              time_id: null,
              multiday_tours_time_ids: null,
              skip_pickup: false,
              pickup_places: null,
              dropoff_places: null,
              deposit: undefined,
              pickup_required: undefined,
              // Pinned false (spec 2026-09-17 §3), unlike pickup_required above:
              // this entry has no served list, so nothing can be required.
              dropoff_required: false,
              start_times: null
            };
          });
  
          const addonAvailabilityResults = addonResults;
          
          // Store results in state
          this._state.addonAvailability.clear();
          addonAvailabilityResults.forEach(result => {
            this._state.addonAvailability.set(result.addon_id, result);
          });
  
          // Auto-select addon that matches main product ID
          const mainProductAddon = this._state.config.productAddons.find(addon => addon.addon_product_id == this._state.config.productId);
          if (mainProductAddon && !this._state.selectedAddon) {
            console.log('[NiceCalendar] Auto-selecting addon that matches main product:', mainProductAddon.addon_title);
            this._state.selectedAddon = mainProductAddon;
            
            // Reset person counts for the auto-selected addon
            this._state.personCounts = {};
            this._getCurrentPersonFields().forEach(field => {
              this._state.personCounts[field.fieldId] = parseInt(field.min) || 0;
            });
            
            // Handle start time selection for the selected addon
            if (this._state.selectedDate) {
              const ymd = this._toYMD(this._state.selectedDate);
              const startTimes = this._getStartTimesForDate(ymd);
              if (startTimes && startTimes.length === 1) {
                this._state.selectedStartTime = startTimes[0];
              } else {
                this._state.selectedStartTime = null;
              }
            }
          }
  
        } catch (error) {
          console.error('Error fetching addon availability:', error);
        } finally {
          this._state.addonLoading = false;
          this._rerender();
        }
      },
  
      // Debounced schedule: wait 800ms after the last month switch before fetching
      _scheduleFetch(y, m0) {
        this._state.pendingYear = y;
        this._state.pendingMonth0 = m0;
        if (this._state.fetchTimer) clearTimeout(this._state.fetchTimer);
        this._state.fetchTimer = setTimeout(() => {
          // Only fetch for the latest requested month
          const Y = this._state.pendingYear, M0 = this._state.pendingMonth0;
          this._fetchAvailability(Y, M0);
        }, 800);
      },
  
      _isAvailable(y, m0, d) {
        const ymd = this._ymd(y, m0, d);
        return this._state.dayAvailMap.get(ymd) === true;
      },
      _capacityFor(y, m0, d) {
        const ymd = this._ymd(y, m0, d);
        return this._state.dayCapacityMap.get(ymd) ?? null;
      },
  
      // --- Render ---
      _render(host) {
        const s = this._state, cfg = s.config;
        const pickupOptions = this._syncPickupSelection();
        const pickupRequired = this._pickupRequired();
        const dropoffOptions = this._syncDropoffSelection();
        const dropoffRequired = this._dropoffRequired();
        const monthName = new Date(s.viewYear, s.viewMonth, 1)
          .toLocaleString(cfg.locale || 'en-IS', { month: 'long', year: 'numeric' });
  
        const onCurrentMonth = (s.viewYear === s.today.getFullYear() && s.viewMonth === s.today.getMonth());
        
        // Check if we're at the MAX_VISIBLE_MONTHS limit
        const today = s.today;
        const currentDate = new Date(s.viewYear, s.viewMonth, 1);
        const calendarLimitMonthsFromNow = new Date(
          today.getFullYear(),
          today.getMonth() + (MAX_VISIBLE_MONTHS - 1),
          1
        );
        const isAtCalendarLimit = currentDate.getTime() >= calendarLimitMonthsFromNow.getTime();
  
        const head = `
          <div class="nt-cal-head">
            <div class="nt-title">
              <select class="nt-month-dropdown" data-act="month-select">
                ${this._generateMonthOptions(s.viewYear, s.viewMonth)}
              </select>
            </div>
            <div class="nt-nav">
              <button class="nt-btn" data-act="prev" ${onCurrentMonth ? 'disabled' : ''}>&larr;</button>
              <button class="nt-btn" data-act="today">Today</button>
              <button class="nt-btn" data-act="next" ${isAtCalendarLimit ? 'disabled' : ''}>&rarr;</button>
            </div>
          </div>
        `;
  
        const base = new Date(2021, 7, 2); // Monday
        const dow = Array.from({length:7},(_,i)=>{
          const d = new Date(base); d.setDate(base.getDate()+i);
          return `<div class="nt-dow">${d.toLocaleString(cfg.locale||'en-IS',{weekday:'short'})}</div>`;
        }).join('');
  
        const first = new Date(s.viewYear, s.viewMonth, 1);
        const startDay = (first.getDay() + 6) % 7; // Monday=0
        const days = this._daysInMonth(s.viewYear, s.viewMonth);
        const cells = [];
  
        for (let i=0;i<startDay;i++) cells.push(`<div class="nt-day nt-empty"></div>`);
  
        for (let d=1; d<=days; d++) {
          const isSel = s.selectedDate &&
                        s.selectedDate.getFullYear()===s.viewYear &&
                        s.selectedDate.getMonth()===s.viewMonth &&
                        s.selectedDate.getDate()===d;
  
          const isPast = this._isPast(s.viewYear, s.viewMonth, d);
          const isToday = this._isToday(s.viewYear, s.viewMonth, d);
          // Spec 2026-09-17 §4: today opens only when the product opts in —
          // the API's capacity and Bókun cutoff still decide availability.
          const selectable = !isPast && (!isToday || s.config.same_day_booking === true);
          const avail = selectable ? this._isAvailable(s.viewYear, s.viewMonth, d) : false;
          const capacity = selectable ? this._capacityFor(s.viewYear, s.viewMonth, d) : null;

          const todayCls = isToday ? 'nt-today ' : '';
          let cls = '';
          let attrs = '';
          if (!selectable) {
            cls = todayCls + 'nt-past';
            attrs = 'disabled';
          } else if (s.loading) {
            // while loading, disable clicks
            cls = todayCls;
            attrs = 'disabled';
          } else if (avail === true) {
            // Check if availability is low (< 5)
            if (capacity !== null && capacity < 5) {
              cls = todayCls + 'nt-available-low';
            } else {
              cls = todayCls + 'nt-available';
            }
            attrs = `data-day="${d}"`;
          } else {
            cls = todayCls + 'nt-unavailable';
            attrs = 'disabled';
          }
  
          cells.push(`
            <div class="nt-day">
              <button ${attrs} class="${cls} ${isSel ? 'nt-selected' : ''}">${d}</button>
            </div>
          `);
        }
        while (cells.length % 7) cells.push(`<div class="nt-day nt-empty"></div>`);
  
        // Calculate total price dynamically (including any booking pricing adjustments)
        let total = 0;
        this._getCurrentPersonFields().forEach(field => {
          const count = s.personCounts[field.fieldId] || 0;
          const price = this._getEffectivePersonPrice(field);
          total += count * price;
        });
        
        // Add extras total
        total += this._calculateExtrasTotal();
        
        const selHuman = s.selectedDate
          ? s.selectedDate.toLocaleDateString(cfg.locale||'en-IS',{weekday:'short',year:'numeric',month:'short',day:'numeric'})
          : 'Please select a date';
  
        const side = `
          <div class="nt-side">
            ${cfg.productAddons && cfg.productAddons.length > 0 && s.selectedDate ? `
            <div>
              <div class="nt-section-title">Available Times</div>
              <div class="nt-addon-buttons">
                ${s.addonLoading ? '<div class="nt-loading">Loading availability...</div>' : ''}
                ${cfg.productAddons.map(addon => {
                  const availability = s.addonAvailability.get(addon.addon_product_id);
                  const isAvailable = availability?.available || false;
                  const capacity = availability?.capacity || 0;
                  const isSelected = s.selectedAddon && s.selectedAddon.addon_product_id == addon.addon_product_id;
                  
                  return `
                    <button 
                      class="nt-addon-btn ${isSelected ? 'nt-addon-btn-selected' : ''} ${!isAvailable ? 'nt-addon-btn-unavailable' : ''}"
                      data-addon-id="${addon.addon_product_id}"
                      ${!isAvailable ? 'disabled' : ''}
                    >
                      <div class="nt-addon-title">${addon.addon_title}</div>
                      <div class="nt-addon-capacity">${isAvailable ? `${this._formatCapacityDisplay(capacity)} available` : 'Unavailable'}</div>
                    </button>
                  `;
                }).join('')}
              </div>
            </div>
            ` : ''}
            <div>
              <div class="nt-section-title">Selected date</div>
              <div class="nt-sub">${selHuman}</div>
            </div>
            ${(() => {
              if (!s.selectedDate) return '';
              const ymd = this._toYMD(s.selectedDate);
              const allStartTimes = this._getAllStartTimesForDate(ymd);
              const availableStartTimes = this._getStartTimesForDate(ymd);
              
              // Don't render if no start times at all
              if (!allStartTimes || allStartTimes.length === 0) return '';
              
              // Auto-select if only one available time
              if (availableStartTimes.length === 1 && (!s.selectedStartTime || s.selectedStartTime.start_time_id !== availableStartTimes[0].start_time_id)) {
                s.selectedStartTime = availableStartTimes[0];
              }
              
              // Show read-only for single available time, dropdown for multiple or when there are unavailable times
              if (availableStartTimes.length === 1 && allStartTimes.length === 1) {
                return `
                  <div>
                    <div class="nt-section-title">Start Time</div>
                    <div class="nt-sub">${availableStartTimes[0].time}</div>
                  </div>
                `;
              }
              
              // Multiple times or some unavailable - show dropdown with disabled options
              const getSlotCapacity = (slot) => {
                return typeof slot.capacity === 'number' ? slot.capacity : (typeof slot.availability === 'number' ? slot.availability : 0);
              };
              
              return `
                <div>
                  <div class="nt-section-title">Start Time</div>
                  <select class="nt-select" data-act="select-start-time">
                    <option value="">Select a time</option>
                    ${allStartTimes.map(slot => {
                      const capacity = getSlotCapacity(slot);
                      const isDisabled = capacity <= 0;
                      const isSelected = s.selectedStartTime?.start_time_id === slot.start_time_id;
                      return `
                        <option value="${slot.start_time_id}" ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}>
                          ${slot.time} ${isDisabled ? '(Unavailable)' : `(${this._formatCapacityDisplay(capacity)} available)`}
                        </option>
                      `;
                    }).join('')}
                  </select>
                </div>
              `;
            })()}
            <div>
              <div class="nt-section-title">Prices</div>
              ${this._getPersonFieldsInDisplayOrder().map(field => {
                const price = this._getEffectivePersonPrice(field);
                return `<div class="nt-row"><span>${field.label}</span><strong>${this._fmtCurrency(price)}</strong></div>`;
              }).join('')}
            </div>
            <div>
              <div class="nt-section-title">Guests</div>
              ${(() => {
                const currentTotal = this._getTotalPersonCount();
                const availableCapacity = this._getAvailableCapacity();
                const canIncrement = currentTotal < availableCapacity;
                
                return this._getPersonFieldsInDisplayOrder().map(field => {
                  const count = s.personCounts[field.fieldId] || 0;
                  
                  return `
                    <div class="nt-row">
                      <span>${field.label}</span>
                      <div class="nt-qty">
                        <button data-qty="${field.fieldId}-">-</button>
                        <input type="text" value="${count}" readonly />
                        <button data-qty="${field.fieldId}+" ${!canIncrement ? 'disabled' : ''}>+</button>
                      </div>
                    </div>
                  `;
                }).join('') + (s.selectedDate ? `
                  <div class="nt-availability-info">
                    <small>Total: ${currentTotal} / ${this._formatCapacityDisplay(availableCapacity)} available</small>
                  </div>
                ` : '');
              })()}
            </div>
            ${!cfg.skip_pickup ? `<div>
              <div class="nt-section-title">Pickup location</div>
              <select class="nt-select" data-pickup>${pickupRequired ? `<option value="" disabled ${s.pickupLocation == null ? 'selected' : ''}>Select a pickup location</option>` : ''}${pickupOptions ? pickupOptions.map((o) => `<option value="${o.value}" ${String(s.pickupLocation) === o.value ? 'selected' : ''}>${o.label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</option>`).join('') : `
                <option value="32465" ${s.pickupLocation === 32465 ? 'selected' : ''}>Not Known Yet (Will Provide Details Later) </option>
                <option value="32467" ${s.pickupLocation === 32467 ? 'selected' : ''}>100 Iceland Hotel - Laugavegur 100 (Pick Up at Bus Stop 9 - Snorrabraut )</option>
                <option value="72934" ${s.pickupLocation === 72934 ? 'selected' : ''}>101 Apartments - Bræðraborgarstígur 24a - Pick up from Bus stop 15 - Vesturbugt</option>
                <option value="32468" ${s.pickupLocation === 32468 ? 'selected' : ''}>101 Guesthouse - Laugarvegur 101 (Pick Up at Bus Stop 9)</option>
                <option value="32469" ${s.pickupLocation === 32469 ? 'selected' : ''}>101 Hostel - Laugavegur 58 (Pick Up at Bus Stop 9 - Snorrabraut )</option>
                <option value="32470" ${s.pickupLocation === 32470 ? 'selected' : ''}>101 Hótel - Hverfisgata 10 (Pick Up at Bus Stop 6)</option>
                <option value="32471" ${s.pickupLocation === 32471 ? 'selected' : ''}>101 Skuggi Guesthouse - Lindargata 50 (Pick Up at Bus Stop 9)</option>
                <option value="32472" ${s.pickupLocation === 32472 ? 'selected' : ''}>105 - A Townhouse Hotel - Háteigsvegur 1 (Pick up at Bus Stop 13)</option>
                <option value="32473" ${s.pickupLocation === 32473 ? 'selected' : ''}>1912 Guesthouse - Skólabrú 2 (Pick Up at Bus Stop 3)</option>
                <option value="32476" ${s.pickupLocation === 32476 ? 'selected' : ''}>3 Sisters Guesthouse - Ránargata 16 (Pick Up at Bus Station 1)</option>
                <option value="100500" ${s.pickupLocation === 100500 ? 'selected' : ''}>41 – A Townhouse Hotel - Laugavegur 41 ( Pick Up at Bus Stop 14)</option>
                <option value="32478" ${s.pickupLocation === 32478 ? 'selected' : ''}>4th Floor Hotel - Laugavegur 101 (Pick Up at Bus Stop 9 - Snorrabraut )</option>
                <option value="81010" ${s.pickupLocation === 81010 ? 'selected' : ''}>A Part of Reykjavík - Njálsgata Apartments (Pick Up at Bus Stop 8)</option>
                <option value="32479" ${s.pickupLocation === 32479 ? 'selected' : ''}>A Part Of Reykjavik Brautarholt Apartments - Brautarholt 2, Reykjavík, Iceland</option>
                <option value="32480" ${s.pickupLocation === 32480 ? 'selected' : ''}>Alba Guesthouse - Eskihlíð 3, 105 Reykjavík, Iceland</option>
                <option value="32481" ${s.pickupLocation === 32481 ? 'selected' : ''}>Alda Hotel - Laugavegur 66-68 (Pick Up at Bus Stop 9 - Snorrabraut )</option>
                <option value="32551" ${s.pickupLocation === 32551 ? 'selected' : ''}>Álfhóll Guesthouse - Ránargata 8 (Pick Up at Bus Stop 1)</option>
                <option value="32482" ${s.pickupLocation === 32482 ? 'selected' : ''}>Alfred´s Apartments - Vitastígur 11 (Pick Up at Bus Stop 9 - Snorrabraut) </option>
                <option value="32483" ${s.pickupLocation === 32483 ? 'selected' : ''}>Alfred´s Studios - Frakkastígur 6a (Pick Up at Bus Stop 14 - Skúlagata) </option>
                <option value="9277118" ${s.pickupLocation === 9277118 ? 'selected' : ''}>Alva Apartments, Hallveigarstigur 1 - (Pick Up at Bus Stop 6)</option>
                <option value="32474" ${s.pickupLocation === 32474 ? 'selected' : ''}>Alva Hotel - Brautarholt 22, 105 Reykjavík (Former 22 Hill Hotel) </option>
                <option value="9277117" ${s.pickupLocation === 9277117 ? 'selected' : ''}>Alva Hotel, Ármúli 5, 108 Reykjavik</option>
                <option value="32497" ${s.pickupLocation === 32497 ? 'selected' : ''}>Apotek Hotel - Austurstræti 16 (Pick Up at Bus Station 3)</option>
                <option value="32498" ${s.pickupLocation === 32498 ? 'selected' : ''}>Arctic Comfort Hótel - Síðumúli 19, 105 Reykjavík, Iceland</option>
                <option value="9082378" ${s.pickupLocation === 9082378 ? 'selected' : ''}>Aurora Museum - Grandagarður 2 - Pick up at Bus Stop 15 - Vesturbugt</option>
                <option value="10166271" ${s.pickupLocation === 10166271 ? 'selected' : ''}>AVA Apartments - Skólavörðustígur 1 (Pick up from Bus stop 6)</option>
                <option value="1940129" ${s.pickupLocation === 1940129 ? 'selected' : ''}>B14 - Rooms and Apartments - Bankastræti 14 (Pick Up at Bus Stop 3)</option>
                <option value="51600" ${s.pickupLocation === 51600 ? 'selected' : ''}>B14 Hostel - Fákafen 11, 108 Reykjavík, Iceland </option>
                <option value="10183226" ${s.pickupLocation === 10183226 ? 'selected' : ''}>B18 Apartments, Brautarholt 18, 105 Reykjavík</option>
                <option value="90081" ${s.pickupLocation === 90081 ? 'selected' : ''}>B2 Apartments by ylma - Brautarholt 2, 105 Reykjavík, Iceland </option>
                <option value="9465506" ${s.pickupLocation === 9465506 ? 'selected' : ''}>Backyard Apartments By Ylma - Laugavegur 56 - Pick up at Bus Stop 9 - Snorrabraut </option>
                <option value="9209108" ${s.pickupLocation === 9209108 ? 'selected' : ''}>Baron's Hostel and Guesthouse, Barónsstígur 47 (Pick Up at Bus Stop 8)</option>
                <option value="32501" ${s.pickupLocation === 32501 ? 'selected' : ''}>Black Pearl Luxury Apartments - Tryggvagata 18 (Pick Up at Bus Stop 4 - Miðbakki )</option>
                <option value="32622" ${s.pickupLocation === 32622 ? 'selected' : ''}>Bolholt Apartments - Bolholt 6, 105 Reykjavík, Iceland</option>
                <option value="10166672" ${s.pickupLocation === 10166672 ? 'selected' : ''}>Briet Apartments - Þingholtsstræti 7 (Pick Up at Bus Stop 6)</option>
                <option value="32503" ${s.pickupLocation === 32503 ? 'selected' : ''}>Brim Hotel - Skipholt 27, 105 Reykjavík, Iceland</option>
                <option value="32504" ${s.pickupLocation === 32504 ? 'selected' : ''}>BSÍ Bus Terminal - Vatnsmýrarvegur 10 , 101 Reykjavík, Iceland</option>
                <option value="32505" ${s.pickupLocation === 32505 ? 'selected' : ''}>Bus Hostel - Skógarhlíð 10, 105 Reykjavík, Iceland</option>
                <option value="32506" ${s.pickupLocation === 32506 ? 'selected' : ''}>Bus Stop 1 (Ráðhúsið, City Hall)</option>
                <option value="32508" ${s.pickupLocation === 32508 ? 'selected' : ''}>Bus Stop 11 (Austurbær)</option>
                <option value="32509" ${s.pickupLocation === 32509 ? 'selected' : ''}>Bus Stop 12 (Höfðaborg)</option>
                <option value="58416" ${s.pickupLocation === 58416 ? 'selected' : ''}>Bus Stop 13 (Rauðarárstígur)</option>
                <option value="100454" ${s.pickupLocation === 100454 ? 'selected' : ''}>Bus Stop 14 (Skúlagata)</option>
                <option value="10069289" ${s.pickupLocation === 10069289 ? 'selected' : ''}>Bus Stop 15 - Vesturbugt / Mýrargata </option>
                <option value="32510" ${s.pickupLocation === 32510 ? 'selected' : ''}>Bus Stop 2 (Tjörnin - The Pond)</option>
                <option value="32511" ${s.pickupLocation === 32511 ? 'selected' : ''}>Bus Stop 3 (Lækjargata)</option>
                <option value="1745120" ${s.pickupLocation === 1745120 ? 'selected' : ''}>Bus Stop 4 (Midbakki)</option>
                <option value="32513" ${s.pickupLocation === 32513 ? 'selected' : ''}>Bus Stop 5 (Harpa)</option>
                <option value="32514" ${s.pickupLocation === 32514 ? 'selected' : ''}>Bus Stop 6 - Safnahúsið / The Culture House</option>
                <option value="32516" ${s.pickupLocation === 32516 ? 'selected' : ''}>Bus Stop 8 (Hallgrímskirkja)</option>
                <option value="32517" ${s.pickupLocation === 32517 ? 'selected' : ''}>Bus Stop 9 (Snorrabraut) </option>
                <option value="32555" ${s.pickupLocation === 32555 ? 'selected' : ''}>Butterfly Guesthouse - Ránargata 8 (Pick Up at Bus Stop 1)</option>
                <option value="32519" ${s.pickupLocation === 32519 ? 'selected' : ''}>Canopy by Hilton Reykjavík - Smiðjustígur 4 (Pick Up at Bus Stop 6)</option>
                <option value="32521" ${s.pickupLocation === 32521 ? 'selected' : ''}>Castle House Luxury Apartments - Skalholtsstigur 2a (Pick Up at Bus Stop 2)</option>
                <option value="10745378" ${s.pickupLocation === 10745378 ? 'selected' : ''}>Center Apartments - Esja, Brautarholti 20, 105 Reykjavik</option>
                <option value="32522" ${s.pickupLocation === 32522 ? 'selected' : ''}>Center Hotel Arnarhvoll - Ingólfsstræti 1 (Pick Up at Bus Stop 6)</option>
                <option value="9977977" ${s.pickupLocation === 9977977 ? 'selected' : ''}>Center Hotel Grandi - Seljavegur 2 - Pick up at Bus Stop 15</option>
                <option value="32523" ${s.pickupLocation === 32523 ? 'selected' : ''}>Center Hotel Klöpp - Klapparstígur 26 (Pick Up at Bus Stop 14)</option>
                <option value="114237" ${s.pickupLocation === 114237 ? 'selected' : ''}>Center Hotel Laugavegur - Laugavegur 95 (Pick Up at Bus Stop 9)</option>
                <option value="32524" ${s.pickupLocation === 32524 ? 'selected' : ''}>Center Hotel Miðgarður - Laugavegur 120 (Pick Up at Bus Stop 13)</option>
                <option value="32525" ${s.pickupLocation === 32525 ? 'selected' : ''}>Center Hotel Plaza - Aðalstræti 6 (Pick Up at Bus Stop 1)</option>
                <option value="32526" ${s.pickupLocation === 32526 ? 'selected' : ''}>Center Hotel Skjaldbreið - Laugavegur 16 (Pick Up at Bus Stop 6)</option>
                <option value="32527" ${s.pickupLocation === 32527 ? 'selected' : ''}>Center Hotel Þingholt - Þingholtsstræti 5 (Pick Up at Bus Stop 6)</option>
                <option value="8332221" ${s.pickupLocation === 8332221 ? 'selected' : ''}>Center Kistufell, Brautarholti 16, 105 Reykjavik</option>
                <option value="1940310" ${s.pickupLocation === 1940310 ? 'selected' : ''}>Central Apartments - Rángargata 4 (Pick Up at Bus Stop 1)</option>
                <option value="32529" ${s.pickupLocation === 32529 ? 'selected' : ''}>Central Guesthouse - Laufásvegur 2 (Pick Up at Bus Stop 2)</option>
                <option value="8407055" ${s.pickupLocation === 8407055 ? 'selected' : ''}>Central Park Guesehouse - Kirkjuteigur 17, 105 Reykjavík </option>
                <option value="1927281" ${s.pickupLocation === 1927281 ? 'selected' : ''}>City Center Hotel - Austurstræti 6 (Pick Up at Bus Stop 1)</option>
                <option value="32533" ${s.pickupLocation === 32533 ? 'selected' : ''}>City Comfort Apartments - Laugavegur 103 (Pick Up at Bus Stop 9 - Snorrabraut) </option>
                <option value="32534" ${s.pickupLocation === 32534 ? 'selected' : ''}>City Park Hotel - Hallarmúli 1, 108 Reykjavík, Iceland</option>
                <option value="11864186" ${s.pickupLocation === 11864186 ? 'selected' : ''}>CityHub Reykjavik, Hverfisgata 46 (Pick up from Bus stop 14)</option>
                <option value="91194" ${s.pickupLocation === 91194 ? 'selected' : ''}>Cruise Ship Arrival (Will Provide Details Later) </option>
                <option value="8364584" ${s.pickupLocation === 8364584 ? 'selected' : ''}>Dalur Hi Hostel &amp; Cafe, Sundlaugavegur 34, 104 Reykjavik</option>
                <option value="13046918" ${s.pickupLocation === 13046918 ? 'selected' : ''}>Day Dream Vesturgata 17 - Pick up from Bus Stop 4 - Miðbakki</option>
                <option value="8810263" ${s.pickupLocation === 8810263 ? 'selected' : ''}>Downtown Guesthouse Reykjavik, Lækjargata 6b - Pick up at (Bus Stop 3)</option>
                <option value="9172976" ${s.pickupLocation === 9172976 ? 'selected' : ''}>Downtown Hostel, Vesturgata 17 - Pick up at Bus Stop 4 - Miðbakki </option>
                <option value="32535" ${s.pickupLocation === 32535 ? 'selected' : ''}>Downtown Reykjavik Apartments - Rauðarárstígur 31 - Pick up at Bus Stop 13</option>
                <option value="70128" ${s.pickupLocation === 70128 ? 'selected' : ''}>Downtown Reykjavik Apartments - Stakkholt 4a - Pick up at Bus Stop 13</option>
                <option value="8944273" ${s.pickupLocation === 8944273 ? 'selected' : ''}>EKRA Apartments - Þingholtsstræti 2-4 - pick up at Bus station 6</option>
                <option value="1935220" ${s.pickupLocation === 1935220 ? 'selected' : ''}>Embassy House - Sólvallagata 63 - Pick up at JL Húsið, Hringbraut 121 (Former Circle Hostel)</option>
                <option value="32536" ${s.pickupLocation === 32536 ? 'selected' : ''}>Embassy Luxury Apartments - Garðastræti 40 (Pick Up at Bus Stop 1)</option>
                <option value="32537" ${s.pickupLocation === 32537 ? 'selected' : ''}>Eric the Red Guesthouse - Eiríksgata 6 (Pick Up at Bus Stop 8)</option>
                <option value="50731" ${s.pickupLocation === 50731 ? 'selected' : ''}>Exeter Hotel by Keahotels - Tryggvagata 12 (Pick Up at Bus Stop 4)</option>
                <option value="32538" ${s.pickupLocation === 32538 ? 'selected' : ''}>Eyja Guldsmeden Hotel - Brautarholt 10, 105 Reykjavík, Iceland</option>
                <option value="11053009" ${s.pickupLocation === 11053009 ? 'selected' : ''}>Floki Guesthouse, Flókagata 1 - Pick up from Bus stop 11 </option>
                <option value="32539" ${s.pickupLocation === 32539 ? 'selected' : ''}>Forsæla House &amp; Apartments - Grettisgata 33b (Pick Up at Bus Stop 8)</option>
                <option value="32540" ${s.pickupLocation === 32540 ? 'selected' : ''}>Fosshótel Barón - Barónsstígur 2-4, 101 Reykjavík, Iceland</option>
                <option value="32541" ${s.pickupLocation === 32541 ? 'selected' : ''}>Fosshótel Lind, Rauðarárstígur 18 – Pick Up At Bus Stop 13 </option>
                <option value="32542" ${s.pickupLocation === 32542 ? 'selected' : ''}>Fosshótel Rauðará, Rauðarárstígur 37 - Pick Up At Bus Stop 13</option>
                <option value="32543" ${s.pickupLocation === 32543 ? 'selected' : ''}>Fosshótel Reykjavík - Þórunnartún 1 (Pick Up at Bus Stop 12)</option>
                <option value="10294965" ${s.pickupLocation === 10294965 ? 'selected' : ''}>Freyja Guesthouse and Suites - Pick up at Bus Stop 8 - Hallgrímskirkja </option>
                <option value="12953539" ${s.pickupLocation === 12953539 ? 'selected' : ''}>Frost Apartments - Skipholt 21, 105 Reykjavik - Direct Pick Up</option>
                <option value="32545" ${s.pickupLocation === 32545 ? 'selected' : ''}>Gallery Central Guesthouse - Ránargata 11 (Pick Up at Bus Stop 1)</option>
                <option value="32547" ${s.pickupLocation === 32547 ? 'selected' : ''}>Grand Hótel Reykjavík, Sigtún 38, 105 Reykjavík, Iceland</option>
                <option value="32548" ${s.pickupLocation === 32548 ? 'selected' : ''}>Grettisborg Apartments - Grettisgata 51 (Pick Up at Bus Stop 11)</option>
                <option value="44474" ${s.pickupLocation === 44474 ? 'selected' : ''}>Grímur Hótel - Efstaland 26, 108 Reykjavík, Iceland</option>
                <option value="8862397" ${s.pickupLocation === 8862397 ? 'selected' : ''}>Guesthouse Andrea - Njarðargata 43 (Pick Up at Bus Stop 8)</option>
                <option value="32549" ${s.pickupLocation === 32549 ? 'selected' : ''}>Guesthouse Anna Reykjavik - Smáragata 16 (Pick Up at BSI Bus Station)</option>
                <option value="32550" ${s.pickupLocation === 32550 ? 'selected' : ''}>Guesthouse Aurora - Freyjugata 24 (Pick Up at Bus Stop 8)</option>
                <option value="32552" ${s.pickupLocation === 32552 ? 'selected' : ''}>Guesthouse Baldursbrá – Laufásvegur 41 (Pick Up at BSI Bus Station) </option>
                <option value="32553" ${s.pickupLocation === 32553 ? 'selected' : ''}>Guesthouse Baldursbrá – Tjarnargata 46 (Pick Up at BSI Bus Station)</option>
                <option value="32554" ${s.pickupLocation === 32554 ? 'selected' : ''}>Guesthouse Borgartún, Borgartún 34, 105 Reykjavík, Iceland</option>
                <option value="114784" ${s.pickupLocation === 114784 ? 'selected' : ''}>Guesthouse Center Kistufell - Brautarholt 16, 105 Reykjavík, Iceland</option>
                <option value="1935231" ${s.pickupLocation === 1935231 ? 'selected' : ''}>Guesthouse Egilsborg - Þverholt 20 - Pick up at Bus Stop 13</option>
                <option value="32556" ${s.pickupLocation === 32556 ? 'selected' : ''}>Guesthouse Galtafell - Laufásvegur 46 (Pick Up at BSI Bus Station)</option>
                <option value="32546" ${s.pickupLocation === 32546 ? 'selected' : ''}>Guesthouse Gestinn - Njarðargata 29 (Pick Up at Bus Stop 8)</option>
                <option value="32590" ${s.pickupLocation === 32590 ? 'selected' : ''}>Guesthouse Laugabjarg - Sundlaugavegur 37, 105 Reykjavík, Iceland</option>
                <option value="32557" ${s.pickupLocation === 32557 ? 'selected' : ''}>Guesthouse Lena Apartment - Fjólugata 11 (Pick Up at BSI Bus Station)</option>
                <option value="8291289" ${s.pickupLocation === 8291289 ? 'selected' : ''}>Guesthouse Norðurstjarnan - Hringbraut 53, 101 Reykjavík </option>
                <option value="32559" ${s.pickupLocation === 32559 ? 'selected' : ''}>Guesthouse Pávi - Brautarholt 4, 105 Reykjavík, Iceland</option>
                <option value="32560" ${s.pickupLocation === 32560 ? 'selected' : ''}>Guesthouse Snorri - Snorrabraut 61 (Pick Up at Bus Stop 11)</option>
                <option value="32561" ${s.pickupLocation === 32561 ? 'selected' : ''}>Guesthouse Summer Day - Grensásvegur 24, 108 Reykjavík, Iceland</option>
                <option value="32628" ${s.pickupLocation === 32628 ? 'selected' : ''}>Guesthouse Sunna - Þórsgata 26 (Pick Up at Bus Stop 8)</option>
                <option value="1015224" ${s.pickupLocation === 1015224 ? 'selected' : ''}>Guesthouse Víkingur - Þverholt 20, 105 Reykjavík, Iceland</option>
                <option value="32562" ${s.pickupLocation === 32562 ? 'selected' : ''}>Harbor - Miðbakki (Pick up at Bus Stop 4 - Miðbakki) </option>
                <option value="32563" ${s.pickupLocation === 32563 ? 'selected' : ''}>Harbor Skarfabakki</option>
                <option value="32564" ${s.pickupLocation === 32564 ? 'selected' : ''}>Harbor Sundabakki </option>
                <option value="9750092" ${s.pickupLocation === 9750092 ? 'selected' : ''}>Heart House Guesthouse, Veltusund - Pick up from Bus Stop 1 - City Hall</option>
                <option value="32565" ${s.pickupLocation === 32565 ? 'selected' : ''}>Heida's Home - Hverfisgata 102 (Pick Up at Bus Stop 9 - Snorrabraut) </option>
                <option value="8279563" ${s.pickupLocation === 8279563 ? 'selected' : ''}>Heilsugæslan Suðurlandsbraut 34, 108 Reykjavik</option>
                <option value="32566" ${s.pickupLocation === 32566 ? 'selected' : ''}>Hilton Reykjavík Nordica - Suðurlandsbraut 2, 108 Reykjavík, Iceland</option>
                <option value="32567" ${s.pickupLocation === 32567 ? 'selected' : ''}>Hlemmur Apartments - Laugavegur 126 (Pick Up at Bus Stop 13 - Rauðarárstígur) </option>
                <option value="32568" ${s.pickupLocation === 32568 ? 'selected' : ''}>Hlemmur Square Hostel - Laugavegur 105 (Pick Up at Bus Stop 13 - Rauðarárstígur) </option>
                <option value="32569" ${s.pickupLocation === 32569 ? 'selected' : ''}>Hostel B47 - Baronsstígur 47 (Pick Up at Bus Stop 8)</option>
                <option value="10524407" ${s.pickupLocation === 10524407 ? 'selected' : ''}>Hótel Aska, Skipholt 29, 105 Reykjvík </option>
                <option value="32570" ${s.pickupLocation === 32570 ? 'selected' : ''}>Hotel Borg by Keahotels - Pósthússtræti 11 (Pick Up at Bus Stop 3)</option>
                <option value="32518" ${s.pickupLocation === 32518 ? 'selected' : ''}>Hotel Cabin - Borgartún 32, 105 Reykjavík, Iceland</option>
                <option value="1939593" ${s.pickupLocation === 1939593 ? 'selected' : ''}>Hótel Fönix - Laugavegur 140 (Pick Up at Bus Stop 13 - Rauðarárstígur) </option>
                <option value="32577" ${s.pickupLocation === 32577 ? 'selected' : ''}>Hótel Frón - Laugavegur 22A (Pick Up at Bus Stop 14)</option>
                <option value="32571" ${s.pickupLocation === 32571 ? 'selected' : ''}>Hotel Hilda - Bárugata 11 (Pick Up at Bus Stop 1)</option>
                <option value="32578" ${s.pickupLocation === 32578 ? 'selected' : ''}>Hótel Holt - Bergstaðastræti 37 (Pick Up at BSI Bus Station)</option>
                <option value="32579" ${s.pickupLocation === 32579 ? 'selected' : ''}>Hotel Ísland – Spa &amp; Wellness Hotel - Ármúli 9, 108 Reykjavík, Iceland</option>
                <option value="32588" ${s.pickupLocation === 32588 ? 'selected' : ''}>Hótel Klettur - Mjölnisholt 12 - 14, 105 Reykjavík, Iceland</option>
                <option value="32572" ${s.pickupLocation === 32572 ? 'selected' : ''}>Hotel Leifur Eiríksson - Skólavörðustígur 45 (Pick Up at Bus Stop 8)</option>
                <option value="32580" ${s.pickupLocation === 32580 ? 'selected' : ''}>Hótel Lotus - Álftamýri 7, 108 Reykjavík, Iceland</option>
                <option value="8191866" ${s.pickupLocation === 8191866 ? 'selected' : ''}>Hótel Múli, Hallarmúli 1, 108 Reykjavík</option>
                <option value="32581" ${s.pickupLocation === 32581 ? 'selected' : ''}>Hótel Örkin - Brautarholt 29, 105 Reykjavík, Iceland</option>
                <option value="32574" ${s.pickupLocation === 32574 ? 'selected' : ''}>Hotel Óðinsvé - Þórsgata 1 (Pick Up at Bus Stop 8)</option>
                <option value="32575" ${s.pickupLocation === 32575 ? 'selected' : ''}>Hotel Reykjavík Centrum - Adalstræti 16 (Pick Up at Bus Stop 1)</option>
                <option value="8664273" ${s.pickupLocation === 8664273 ? 'selected' : ''}>Hotel Reykjavik Saga, Lækjargata 12 (Pick up from Bus stop 3)</option>
                <option value="9298683" ${s.pickupLocation === 9298683 ? 'selected' : ''}>Hótel Vera - Laugarvegur 74 (Pick Up at Bus Stop 9)</option>
                <option value="602764" ${s.pickupLocation === 602764 ? 'selected' : ''}>Hotel Von - Laugavegur 55 (Pick Up at Bus Stop 9 - Snorrabraut) </option>
                <option value="32576" ${s.pickupLocation === 32576 ? 'selected' : ''}>House Of The Spirits - Ránargata 1 (Pick Up at Bus Stop 1)</option>
                <option value="68302" ${s.pickupLocation === 68302 ? 'selected' : ''}>Ice Apartments Reykjavík - Laugavegur 1 (Pick Up at Bus Stop 3)</option>
                <option value="9239775" ${s.pickupLocation === 9239775 ? 'selected' : ''}>Iceland Parliament Hotel By Hilton - Pick up at Bus Stop 1 - City Hall </option>
                <option value="45941" ${s.pickupLocation === 45941 ? 'selected' : ''}>Igdlo Guesthouse - Gunnarsbraut 46 (Pick Up at BSI Bus Station)</option>
                <option value="51601" ${s.pickupLocation === 51601 ? 'selected' : ''}>Ion City Hotel - Laugavegur 28 (Pick Up at Bus Stop 14)</option>
                <option value="32585" ${s.pickupLocation === 32585 ? 'selected' : ''}>Ísland Apartments - Hverfisgata 56 (Pick Up at Bus Station 14)</option>
                <option value="9251411" ${s.pickupLocation === 9251411 ? 'selected' : ''}>Iðunn Apartments, Laugavegur 42 - Pick up at Bus Stop 14</option>
                <option value="32602" ${s.pickupLocation === 32602 ? 'selected' : ''}>JL húsið (former Circle Hostel), Hringbraut 121, 101 Reykjavík, Iceland</option>
                <option value="32586" ${s.pickupLocation === 32586 ? 'selected' : ''}>Kerno Apartments - Hringbraut 79 - Pick up at Circle Hostel, Hringbraut 121</option>
                <option value="32587" ${s.pickupLocation === 32587 ? 'selected' : ''}>KEX Hostel - Skúlagata 28, 101 Reykjavík, Iceland</option>
                <option value="32589" ${s.pickupLocation === 32589 ? 'selected' : ''}>Kvosin Downtown Hotel - Kirkjutorg (Pick Up at Bus Stop 1)</option>
                <option value="32591" ${s.pickupLocation === 32591 ? 'selected' : ''}>Lighthouse Apartments - Ránargata 9a (Pick Up at Bus Stop 4)</option>
                <option value="32573" ${s.pickupLocation === 32573 ? 'selected' : ''}>Local 101 Hotel - Ránargata 4A (Pick Up at Bus Stop 1)</option>
                <option value="32592" ${s.pickupLocation === 32592 ? 'selected' : ''}>LOFT Hostel - Bankastræti 7 (Pick Up at Bus Stop 6)</option>
                <option value="1940279" ${s.pickupLocation === 1940279 ? 'selected' : ''}>Luna Hotel Apartments - Laugavegur 37 (Pick Up at Bus Station 14)</option>
                <option value="91965" ${s.pickupLocation === 91965 ? 'selected' : ''}>Lækur Guesthouse - Hrísateigur 47, 105 Reykjavík, Iceland </option>
                <option value="63417" ${s.pickupLocation === 63417 ? 'selected' : ''}>Lækur Hostel - Laugarnesvegur 74a, 105 Reykjavík, Iceland</option>
                <option value="63100" ${s.pickupLocation === 63100 ? 'selected' : ''}>Marina Apartments - Nýlendugata 18, 101 Reykjavík - Pick up at Bus stop 4 - Miðbakki </option>
                <option value="8502452" ${s.pickupLocation === 8502452 ? 'selected' : ''}>Marriott Reykjavik EDITION - Pick up at Bus stop 4</option>
                <option value="32597" ${s.pickupLocation === 32597 ? 'selected' : ''}>Mengi Apartments - Frakkastígur 14A (Pick Up at Bus Stop 8)</option>
                <option value="12301414" ${s.pickupLocation === 12301414 ? 'selected' : ''}>Midtown Hotel by Heimaleiga, Vegamótastígur 9 – Pick up at Bus Stop 8</option>
                <option value="1940132" ${s.pickupLocation === 1940132 ? 'selected' : ''}>N1 Station (Ártúnsbrekka)</option>
                <option value="81011" ${s.pickupLocation === 81011 ? 'selected' : ''}>Nest Apartments - Bergþórugata 15 (Pick Up at Bus Stop 8)</option>
                <option value="1940127" ${s.pickupLocation === 1940127 ? 'selected' : ''}>New Apartments - Ásholt 2, 105 Reykjavík, Iceland</option>
                <option value="1940128" ${s.pickupLocation === 1940128 ? 'selected' : ''}>Next Apartment - Bergstaðastræti 9b (Pick Up at Bus Stop 6)</option>
                <option value="32466" ${s.pickupLocation === 32466 ? 'selected' : ''}>NICETRAVEL Office - Fiskislóð 45, 101 Reykjavík, Iceland</option>
                <option value="32600" ${s.pickupLocation === 32600 ? 'selected' : ''}>Northern Comfort Apartments - 15 Skipholt, 105 Reykjavík, Iceland </option>
                <option value="1935218" ${s.pickupLocation === 1935218 ? 'selected' : ''}>Norðurey Guesthouse - Snorrabraut 71 (Pick Up at Bus Stop 11)</option>
                <option value="32599" ${s.pickupLocation === 32599 ? 'selected' : ''}>Norðurey Hótel Laugardalur - Hrísateigur 14, 105 Reykjavík, Iceland</option>
                <option value="11588041" ${s.pickupLocation === 11588041 ? 'selected' : ''}>Oddsson Downtown, Háteigsvegur 1 – Pick up at Bus Stop 13</option>
                <option value="108403" ${s.pickupLocation === 108403 ? 'selected' : ''}>Oddsson MIDTOWN Hótel - Grensásvegur 16, 104 Reykjavík, Iceland</option>
                <option value="32603" ${s.pickupLocation === 32603 ? 'selected' : ''}>OK Hotel - Laugavegur 74 (Pick Up at Bus Stop 9 - Snorrabraut) </option>
                <option value="32604" ${s.pickupLocation === 32604 ? 'selected' : ''}>Old Charm Apartments - Vatnsstígur 10b (Pick Up at Bus Stop 14)</option>
                <option value="9830934" ${s.pickupLocation === 9830934 ? 'selected' : ''}>Old Saga Hotel - Hagatorg </option>
                <option value="9929250" ${s.pickupLocation === 9929250 ? 'selected' : ''}>Old Town Reykjavik, Kirkjustræti 2 (Pick up from Bus stop 1)</option>
                <option value="32558" ${s.pickupLocation === 32558 ? 'selected' : ''}>Óðinn Reykjavík Apartments - Óðinsgata 9 (Pick Up at Bus Stop 8)</option>
                <option value="1940311" ${s.pickupLocation === 1940311 ? 'selected' : ''}>Planet Apartments / AirBnBs &amp; Apartments - Ægisgata 5 (Pick Up at Bus stop 15 - Vesturbugt)</option>
                <option value="165952" ${s.pickupLocation === 165952 ? 'selected' : ''}>R13 - A Townhouse Hotel - Ármúli 13a, 105 Reykjavík, Iceland </option>
                <option value="32607" ${s.pickupLocation === 32607 ? 'selected' : ''}>Radisson Blu Hotel 1919 - Pósthússtræti 2 (Pick Up at Bus Stop 3)</option>
                <option value="3607906" ${s.pickupLocation === 3607906 ? 'selected' : ''}>Refurinn Guesthouse - Ægisgata 26 - Pick up from Bus Stop 1 - City Hall </option>
                <option value="32609" ${s.pickupLocation === 32609 ? 'selected' : ''}>REK Inn - Skipholt 15, 105 Reykjavík, Iceland</option>
                <option value="1940080" ${s.pickupLocation === 1940080 ? 'selected' : ''}>Retro Hostel - Skógarhlíð 20, 105 Reykjavík, Iceland</option>
                <option value="1940130" ${s.pickupLocation === 1940130 ? 'selected' : ''}>Rey Apartments - Brautarholt 2, 105 Reykjavík, Iceland</option>
                <option value="32610" ${s.pickupLocation === 32610 ? 'selected' : ''}>Rey Apartments - Grettisgata 2a (Pick Up at Bus Stop 8)</option>
                <option value="8810286" ${s.pickupLocation === 8810286 ? 'selected' : ''}>Reykjavik Downtown Guest House, 3 Egilsgata - Pick up at Bus Stop 8</option>
                <option value="8196360" ${s.pickupLocation === 8196360 ? 'selected' : ''}>Reykjavik Downtown Hotel, Skolavordustigur 42, (Bus Stop 8)</option>
                <option value="32613" ${s.pickupLocation === 32613 ? 'selected' : ''}>Reykjavík Hostel Village - Flókagata 1 (Pick Up at Bus Stop 11)</option>
                <option value="50389" ${s.pickupLocation === 50389 ? 'selected' : ''}>Reykjavik Konsulat Hotel, Hafnarstraeti 17-19 - Pick Up at Bus Stop 3 - Lækjaragata</option>
                <option value="32614" ${s.pickupLocation === 32614 ? 'selected' : ''}>Reykjavik Lights by Keahotels - Suðurlandsbraut 12, 108 Reykjavík, Iceland</option>
                <option value="32582" ${s.pickupLocation === 32582 ? 'selected' : ''}>Reykjavík Marina - Berjaya Iceland Hotel Mýrargata 2 (Pick Up at Bus Stop 15)</option>
                <option value="32583" ${s.pickupLocation === 32583 ? 'selected' : ''}>Reykjavík Natura - Berjaya Iceland Hotels, Nauthólsvegur 52, 101 Reykjavík, Iceland</option>
                <option value="32615" ${s.pickupLocation === 32615 ? 'selected' : ''}>Reykjavík Residence Hotel - Hverfisgata 60 (Pick Up at Bus Stop 14)</option>
                <option value="32616" ${s.pickupLocation === 32616 ? 'selected' : ''}>Reykjavík Residence Suites - Hverfisgata 21 (Pick Up at Bus Stop 6)</option>
                <option value="9629410" ${s.pickupLocation === 9629410 ? 'selected' : ''}>Reykjavik Treasure B&amp;B, Fishersund 3 - Pick up from Bus Stop 1 - City Hall </option>
                <option value="32617" ${s.pickupLocation === 32617 ? 'selected' : ''}>Reykjavik4you Aparatments – Bergstaðastræti 12 (Pick Up at Bus Stop 6)</option>
                <option value="32618" ${s.pickupLocation === 32618 ? 'selected' : ''}>Reykjavik4you Apartments – Laugavegur 85 (Pick Up at Bus Stop 9 - Snorrabraut) </option>
                <option value="32619" ${s.pickupLocation === 32619 ? 'selected' : ''}>Room With A View Apartments - Laugavegur 18 (Pick Up at Bus Stop 6)</option>
                <option value="284435" ${s.pickupLocation === 284435 ? 'selected' : ''}>Saga Studios by ylma - Barónsstígur 28 (Pick Up at Bus Stop 11)</option>
                <option value="32620" ${s.pickupLocation === 32620 ? 'selected' : ''}>Sand Hotel by Keahotels - Laugavegur 34 (Pick Up at Bus Stop 14)</option>
                <option value="1938358" ${s.pickupLocation === 1938358 ? 'selected' : ''}>Siddy Apartment - Flókagata 39 - Pick up from Bus Stop 11 - Austurbær</option>
                <option value="116794" ${s.pickupLocation === 116794 ? 'selected' : ''}>SIF Apartments - Bríetartún 9 (Pick Up at Bus Stop 12)</option>
                <option value="9902840" ${s.pickupLocation === 9902840 ? 'selected' : ''}>Skólavörðustígur Apartments (Pick up from Bus stop 8)</option>
                <option value="9902682" ${s.pickupLocation === 9902682 ? 'selected' : ''}>Skólavörðustígur Apartments, Skólavörðurstígur 21a - Pick up at Bus Stop 8 - Hallgrímskirkja</option>
                <option value="32621" ${s.pickupLocation === 32621 ? 'selected' : ''}>Skuggi Hotel by Keahotels - Hverfisgata 103 (Pick up at Bus Stop 9)</option>
                <option value="8333886" ${s.pickupLocation === 8333886 ? 'selected' : ''}>SM Hostel, Skógarhlíð 20, 105 Reykjavík </option>
                <option value="8278859" ${s.pickupLocation === 8278859 ? 'selected' : ''}>Smokey Bay Guesthouse, Ármúla 34, 108 Reykjavik</option>
                <option value="50813" ${s.pickupLocation === 50813 ? 'selected' : ''}>Sport Hostel - Álfheimar 74, 104 Reykjavik, Iceland</option>
                <option value="1940081" ${s.pickupLocation === 1940081 ? 'selected' : ''}>Star Guesthouse - Miðtún 46, 105 Reykjavík, Iceland</option>
                <option value="32623" ${s.pickupLocation === 32623 ? 'selected' : ''}>Stay Apartments - Einholt 2 - Pick up at Bus Stop 13 - Rauðarárstígur</option>
                <option value="32624" ${s.pickupLocation === 32624 ? 'selected' : ''}>Stay Apartments - Grettisgata 43 (Pick Up at Bus Stop 11)</option>
                <option value="2235356" ${s.pickupLocation === 2235356 ? 'selected' : ''}>Stay Apartments - Laugavegur 139 - Pick up from Bus Stop 13 - Rauðarárstígur</option>
                <option value="32626" ${s.pickupLocation === 32626 ? 'selected' : ''}>Storm Hotel - Þórunnartún (Pick Up at Bus Stop 12)</option>
                <option value="32627" ${s.pickupLocation === 32627 ? 'selected' : ''}>Student Hostel - Hringbraut 29, 101 Reykjavík, Iceland</option>
                <option value="8286491" ${s.pickupLocation === 8286491 ? 'selected' : ''}>Svala Apartments - Laugavegur 71 (Pick up at Bus Stop 9 - Snorrabraut) </option>
                <option value="1938353" ${s.pickupLocation === 1938353 ? 'selected' : ''}>The Old Bicycle Shop Guesthouse - Spítalastígur 8 (Pick Up at Bus Stop 8)</option>
                <option value="8607822" ${s.pickupLocation === 8607822 ? 'selected' : ''}>The Reykjavik EDITION Hotel - Pick up from Bus Stop 4 - Miðbakki </option>
                <option value="32629" ${s.pickupLocation === 32629 ? 'selected' : ''}>The Swan House Hotel by ylma - Lindargata 34-36 (Pick Up at Bus Stop 14)</option>
                <option value="9209107" ${s.pickupLocation === 9209107 ? 'selected' : ''}>Theater Row Apartments, Lindargata 14 (Pick Up at Bus Stop 14)</option>
                <option value="83603" ${s.pickupLocation === 83603 ? 'selected' : ''}>Thor Guesthouse - Skólavörðustígur 16 (Pick Up at Bus Stop 8)</option>
                <option value="32630" ${s.pickupLocation === 32630 ? 'selected' : ''}>Tower Guesthouse - Grettisgata 22c, Reykjavik 101 (Bus Stop 8 - Hallgrímskirkja)</option>
                <option value="60005" ${s.pickupLocation === 60005 ? 'selected' : ''}>Tower Suites Reykjavík - Katrínartún 2 (Pick Up at Bus Stop 12)</option>
                <option value="32631" ${s.pickupLocation === 32631 ? 'selected' : ''}>Town House, Laugateigur 26, 105 Reykjavík, Iceland</option>
                <option value="32632" ${s.pickupLocation === 32632 ? 'selected' : ''}>Travel Inn - Sóleyjargata 31 (Pick Up at BSI Bus Station)</option>
                <option value="9309918" ${s.pickupLocation === 9309918 ? 'selected' : ''}>Týr apartments - Grettisgata 62 (Pick Up at Bus Stop 9)</option>
  <option value="8642693" ${s.pickupLocation === 8642693 ? 'selected' : ''}>Vintage Hotel - Laugavegur 41 (Pick up from Bus stop 14)</option>
              `}</select>
            </div>
            ` : ''}
            ${dropoffOptions ? `<div>
              <div class="nt-section-title">Dropoff location</div>
              <select class="nt-select" data-dropoff>${dropoffRequired ? `<option value="" disabled ${s.dropoffLocation == null ? 'selected' : ''}>Select a dropoff location</option>` : ''}${dropoffOptions.map((o) => `<option value="${o.value}" ${String(s.dropoffLocation) === o.value ? 'selected' : ''}>${o.label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</option>`).join('')}</select>
            </div>` : ''}
            ${cfg.extras ? this._renderExtras() : ''}
            ${this._depositOffered() ? `
              <div>
                <div class="nt-section-title">Payment Option</div>
                <div class="nt-deposit-options">
                  <label class="nt-deposit-option ${s.depositOption === 'full' ? 'selected' : ''}" data-deposit="full">
                    <input type="radio" name="deposit" value="full" ${s.depositOption === 'full' ? 'checked' : ''} class="nt-deposit-radio">
                    <div class="nt-deposit-content">
                      <div class="nt-deposit-title">Full Payment</div>
                      <div class="nt-deposit-desc">Pay the complete amount now</div>
                    </div>
                  </label>
                  <label class="nt-deposit-option ${s.depositOption === 'deposit' ? 'selected' : ''}" data-deposit="deposit">
                    <input type="radio" name="deposit" value="deposit" ${s.depositOption === 'deposit' ? 'checked' : ''} class="nt-deposit-radio">
                    <div class="nt-deposit-content">
                      <div class="nt-deposit-title">20% Deposit</div>
                      <div class="nt-deposit-desc">Pay 20% now, balance due later</div>
                    </div>
                  </label>
                </div>
              </div>
            ` : ''}
            <div class="nt-total"><span>Total</span><span>${this._fmtCurrency(total)}</span></div>
            <div class="nt-book">
              ${(() => {
                const gate = this._bookGate();
                const buttonText = gate.loading ? `<div class="nt-book-spinner"></div>` : gate.label;
                return `<button data-act="book" ${gate.canBook ? '' : 'disabled'} class="${gate.loading ? 'nt-book-loading' : ''}">${buttonText}</button>`;
              })()}
            </div>
          </div>
        `;
  
        host.innerHTML = `
          <div class="nt-wrap">
            <div class="nt-top">
              <div class="nt-cal">
                ${head}
                <div class="nt-grid">${dow}</div>
                <div class="nt-grid">${cells.join('')}</div>
                ${s.loading ? `<div class="nt-loading"><div class="nt-spinner"></div>Loading availability…</div>` : ``}
              </div>
              ${side}
            </div>
          </div>
        `;
  
        // Events
        host.querySelectorAll('.nt-btn[data-act]').forEach(btn => {
          btn.addEventListener('click', () => {
            const act = btn.getAttribute('data-act');
            if (act === 'prev') this._shiftMonth(-1, host);
            if (act === 'next') this._shiftMonth(1, host);
            if (act === 'today') {
              const t = new Date();
              // Jump to current month; schedule fetch
              this._state.viewYear = t.getFullYear();
              this._state.viewMonth = t.getMonth();
              this._state.selectedDate = null;
              this._state.selectedStartTime = null;
              this._scheduleFetch(this._state.viewYear, this._state.viewMonth);
              this._render(host);
            }
          });
        });

        // Month dropdown event handler
        host.querySelectorAll('.nt-month-dropdown[data-act="month-select"]').forEach(dropdown => {
          dropdown.addEventListener('change', () => {
            const value = dropdown.value;
            if (value) {
              const [year, month] = value.split('-').map(Number);
              this._state.viewYear = year;
              this._state.viewMonth = month;
              this._state.selectedDate = null;
              this._state.selectedStartTime = null;
              this._scheduleFetch(year, month);
              this._render(host);
            }
          });
        });
  
        host.querySelectorAll('.nt-day button[data-day]').forEach(btn => {
          btn.addEventListener('click', async () => {
            const d = +btn.getAttribute('data-day');
            console.log('[NiceCalendar] Day clicked:', d);
            this._state.selectedDate = new Date(this._state.viewYear, this._state.viewMonth, d);
            console.log('[NiceCalendar] Selected date:', this._state.selectedDate);
            
            // Fetch addon availability for the selected date
            await this._fetchAddonAvailability(this._state.selectedDate);
            
            // Handle start time selection based on available start times
            const ymd = this._toYMD(this._state.selectedDate);
            const startTimes = this._getStartTimesForDate(ymd);
            
            if (startTimes && startTimes.length === 1) {
              // Auto-select single start time
              this._state.selectedStartTime = startTimes[0];
            } else {
              // Reset selection for multiple times or no times
              this._state.selectedStartTime = null;
            }
            
            this._render(host);
          });
        });
        
        // Start time selection handler
        host.querySelectorAll('select[data-act="select-start-time"]').forEach(select => {
          select.addEventListener('change', (e) => {
            const startTimeId = e.target.value;
            if (!startTimeId) {
              // Empty selection
              s.selectedStartTime = null;
              this._render(host);
              return;
            }
            
            const ymd = this._toYMD(s.selectedDate);
            // Only allow selection from available slots (capacity > 0)
            const availableStartTimes = this._getStartTimesForDate(ymd);
            const selectedSlot = availableStartTimes.find(slot => slot.start_time_id === startTimeId);
            
            if (selectedSlot) {
              s.selectedStartTime = selectedSlot;
              this._render(host);
            } else {
              // Selected a disabled option - reset to null
              s.selectedStartTime = null;
              // Reset dropdown to empty selection
              e.target.value = '';
              this._render(host);
            }
          });
        });
  
        host.querySelectorAll('.nt-qty button[data-qty]').forEach(btn => {
          btn.addEventListener('click', () => {
            const t = btn.getAttribute('data-qty');
            
            // Check if this is an extras occupancy control (contains underscore)
            if (t && t.includes('_')) {
              const match = t.match(/^(.+)_(.+)([+-])$/);
              if (match) {
                const fieldKey = match[1];
                const personType = match[2];
                const action = match[3];
                
                if (!this._state.extrasOccupancy.has(fieldKey)) {
                  this._state.extrasOccupancy.set(fieldKey, new Map());
                }
                
                const occupancy = this._state.extrasOccupancy.get(fieldKey);
                const currentCount = occupancy.get(personType) || 0;
                
                let newCount = currentCount;
                if (action === '+') {
                  // Find max count for this person type
                  const personFields = this._getCurrentPersonFields();
                  const personField = personFields.find(pf => pf.viPersonType === personType);
                  let maxCount = 0;
                  
                  if (personField) {
                    // Use the count from the current product's person fields
                    maxCount = this._state.personCounts[personField.fieldId] || 0;
                  } else {
                    // If person type not found in current product, check if it exists in any addon
                    // and use a reasonable default or the total capacity
                    if (personType === 'teenager') {
                      // For teenager, allow up to the total capacity if not in main product
                      maxCount = this._getAvailableCapacity();
                    } else {
                      maxCount = 0;
                    }
                  }
                  
                  newCount = Math.min(currentCount + 1, maxCount);
                } else if (action === '-') {
                  newCount = Math.max(currentCount - 1, 0);
                }
                
                occupancy.set(personType, newCount);
                this._rerender();
                return; // Exit early for extras controls
              }
            }
            
            // Handle main person field controls
            const match = t.match(/^(.+)([+-])$/);
            if (match) {
              const fieldId = match[1];
              const operation = match[2];
              const field = this._getCurrentPersonFields().find(f => f.fieldId == fieldId);
              if (field) {
                const currentCount = this._state.personCounts[fieldId] || 0;
                const minCount = parseInt(field.min) || 0;
                const maxCount = parseInt(field.max) || 99;
                
                if (operation === '-') {
                  this._state.personCounts[fieldId] = Math.max(minCount, currentCount - 1);
                } else if (operation === '+') {
                  // Check availability before allowing increment
                  const currentTotal = this._getTotalPersonCount();
                  const availableCapacity = this._getAvailableCapacity();
                  
                  if (currentTotal < availableCapacity) {
                    this._state.personCounts[fieldId] = Math.min(maxCount, currentCount + 1);
                  } else {
                    console.log('[NiceCalendar] Cannot add more people - capacity limit reached');
                    // You could show a user-friendly message here
                  }
                }
                this._render(host);
              }
            }
          });
        });
  
        const pickupSelect = host.querySelector('select[data-pickup]');
        if (pickupSelect) {
          pickupSelect.addEventListener('change', () => {
            const value = pickupSelect.value;
            if (value === '') {
              this._clearPickupSelection();
              this._state.pickupChosenByUser = false;
            } else {
              this._state.pickupLocation = parseInt(value);
              // Store the selected option's text as the pickup location label
              const selectedOption = pickupSelect.options[pickupSelect.selectedIndex];
              this._state.pickupLocationLabel = selectedOption ? selectedOption.textContent.trim() : 'Unknown Pickup Location';
              this._state.pickupChosenByUser = true;
            }
            this._updateBookButton();
          });
        }

        const dropoffSelect = host.querySelector('select[data-dropoff]');
        if (dropoffSelect) {
          dropoffSelect.addEventListener('change', () => {
            const value = dropoffSelect.value;
            if (value === '') {
              this._clearDropoffSelection();
              this._state.dropoffChosenByUser = false;
            } else {
              this._state.dropoffLocation = parseInt(value);
              const selectedOption = dropoffSelect.options[dropoffSelect.selectedIndex];
              this._state.dropoffLocationLabel = selectedOption ? selectedOption.textContent.trim() : 'Unknown Dropoff Location';
              this._state.dropoffChosenByUser = true;
            }
            this._updateBookButton();
          });
        }
  
        host.querySelectorAll('.nt-deposit-option').forEach(option => {
          option.addEventListener('click', () => {
            const value = option.getAttribute('data-deposit');
            this._state.depositOption = value;
            this._render(host);
          });
        });
  
        // Addon button selection handler
        host.querySelectorAll('.nt-addon-btn[data-addon-id]').forEach(btn => {
          btn.addEventListener('click', () => {
            const addonId = btn.getAttribute('data-addon-id');
            const availability = this._state.addonAvailability.get(parseInt(addonId));
            
            if (!availability?.available) {
              return; // Don't allow selection of unavailable addons
            }
            
            // Find the selected addon
            this._state.selectedAddon = cfg.productAddons.find(addon => addon.addon_product_id == addonId);
            
            // Reset person counts when switching addons
            this._state.personCounts = {};
            this._getCurrentPersonFields().forEach(field => {
              this._state.personCounts[field.fieldId] = parseInt(field.min) || 0;
            });
            
            // Handle start time selection for the selected addon
            if (s.selectedDate) {
              const ymd = this._toYMD(s.selectedDate);
              const startTimes = this._getStartTimesForDate(ymd);
              if (startTimes && startTimes.length === 1) {
                s.selectedStartTime = startTimes[0];
              } else {
                s.selectedStartTime = null;
              }
            }
            
            this._render(host);
          });
        });
  
        // Extras event handlers
        this._setupExtrasEventHandlers(host);
  
        const bookBtn = host.querySelector('button[data-act="book"]');
        if (bookBtn) bookBtn.addEventListener('click', () => this._book());
      },
  
      // Setup event handlers for extras fields
      _setupExtrasEventHandlers(host) {
        if (!this._state.config.extras) return;
  
        // Handle checkbox group changes
        host.querySelectorAll('.nt-extras-checkbox input[type="checkbox"]').forEach(checkbox => {
          checkbox.addEventListener('change', () => {
            const fieldId = checkbox.getAttribute('data-field-id');
            const optionValue = checkbox.getAttribute('data-option-value');
            
            let currentValues = this._state.extrasValues.get(fieldId) || [];
            if (!Array.isArray(currentValues)) {
              currentValues = currentValues ? [currentValues] : [];
            }
            
            if (checkbox.checked) {
              if (!currentValues.includes(optionValue)) {
                currentValues.push(optionValue);
              }
            } else {
              currentValues = currentValues.filter(v => v !== optionValue);
            }
            
            this._state.extrasValues.set(fieldId, currentValues);
            this._updateExtrasVisibility();
            this._rerender();
          });
        });
  
              // Handle select changes
              host.querySelectorAll('.nt-extras-select').forEach(select => {
                select.addEventListener('change', () => {
                  const fieldId = select.getAttribute('data-field-id');
                  const value = select.value;
                  
                  this._state.extrasValues.set(fieldId, value);
                  this._updateExtrasVisibility();
                  this._rerender();
                });
              });
  
              // Handle shoe size select changes
              host.querySelectorAll('.nt-extras-shoe-size-select').forEach(select => {
                select.addEventListener('change', () => {
                  const sizeKey = select.getAttribute('data-size-key');
                  const value = select.value;
                  
                  console.log('[Shoe Size] Setting size:', sizeKey, '=', value);
                  this._state.extrasShoeSizes.set(sizeKey, value);
                  console.log('[Shoe Size] Current extrasShoeSizes:', this._state.extrasShoeSizes);
                  this._rerender();
                });
              });
  
              // Handle textarea changes
              host.querySelectorAll('.nt-extras-textarea').forEach(textarea => {
                textarea.addEventListener('input', () => {
                  const fieldId = textarea.getAttribute('data-field-id');
                  const value = textarea.value;
                  
                  this._state.extrasValues.set(fieldId, value);
                  this._updateExtrasVisibility();
                  // Only update button state, don't re-render entire form (preserves textarea focus)
                  this._updateBookButton();
                });
              });
  
      },
  
      _rerender() { if (this._state._hostEl) this._render(this._state._hostEl); },
      
      // ----- Spec 2026-09-11 §4: deposit offer + required pickup -----

      // Normalise whatever an availability response (or a hand-built variant
      // entry) carries. Missing = offered, so an older backend behaves as
      // today; anything malformed fails closed — a typo must never widen a
      // deposit offer (spec 2026-09-11 decision 4).
      // Runs at intake (so a malformed rule warns at fetch time) and again on every read.
      _depositRuleFrom(raw) {
        const offered = { offered: true, from: null, to: null };
        const notOffered = { offered: false, from: null, to: null };
        if (raw == null) return offered;
        if (typeof raw === 'boolean') return raw ? offered : notOffered;
        const invalid = () => {
          // _depositRule() runs on every render — one warning per page is enough.
          if (!this._state._depositRuleWarned) {
            console.warn('[NiceCalendar] invalid deposit rule — not offering a deposit', raw);
            this._state._depositRuleWarned = true;
          }
          return notOffered;
        };
        if (typeof raw !== 'object' || Array.isArray(raw)) return invalid();
        // Decision 4: an object carrying none of the three keys is a typo, not a rule.
        if (!('offered' in raw) && !('from' in raw) && !('to' in raw)) return invalid();
        if ('offered' in raw && typeof raw.offered !== 'boolean') return invalid();
        // null / '' = open bound; false = present but not a real YYYY-MM-DD date
        const bound = (v) => {
          if (v == null || v === '') return null;
          if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
          const d = new Date(`${v}T00:00:00Z`);
          return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v ? v : false;
        };
        const from = bound(raw.from);
        const to = bound(raw.to);
        if (from === false || to === false) return invalid();
        if (from && to && from > to) return invalid();
        return { offered: 'offered' in raw ? raw.offered === true : true, from, to };
      },

      // The rule that applies right now: the selected variant's, else the
      // product's — also when the variant's availability never arrived
      // (its stored entry is `{ days: [] }`) — unlike `skip_pickup`, which that fallback entry pins to `false`.
      _depositRule() {
        const s = this._state;
        const entry = s.selectedAddon
          ? s.addonAvailability?.get(s.selectedAddon.addon_product_id)
          : null;
        return this._depositRuleFrom(entry?.deposit ?? s.config?.deposit);
      },

      // Deposit offered for the selected travel date: inside the product's
      // date range and still more than 30 days out (the balance is collected
      // before travel).
      _depositOffered() {
        const s = this._state;
        if (!s.selectedDate || !this._isMoreThan30DaysFuture(s.selectedDate)) return false;
        const rule = this._depositRule();
        if (!rule.offered) return false;
        return this._ymdInRange(this._toYMD(s.selectedDate), rule.from, rule.to);
      },

      // skip_pickup for what is being booked: the variant's answer, else the
      // product's (mirrors _book's `?? cfg.skip_pickup`).
      _skipPickup() {
        const s = this._state, cfg = s.config;
        const flag = s.selectedAddon
          ? (s.addonAvailability?.get(s.selectedAddon.addon_product_id)?.skip_pickup ?? cfg?.skip_pickup)
          : cfg?.skip_pickup;
        return flag === true;
      },

      // The customer must choose a pickup place (spec 2026-09-11 §4). A
      // hidden pickup can never be required: _render draws the select from
      // the main product's skip_pickup and _book posts from the variant's,
      // so both must be false. Raw variant values resolve at read time.
      _pickupRequired() {
        const s = this._state;
        const entry = s.selectedAddon
          ? s.addonAvailability?.get(s.selectedAddon.addon_product_id)
          : null;
        const required = (entry?.pickup_required ?? s.config?.pickup_required) === true;
        if (s.config?.skip_pickup === true) {
          // The product hides the select, so a variant asking for a pickup
          // leaves the customer nothing to satisfy. One warning per page:
          // _pickupRequired() runs on every render.
          if (required && s.selectedAddon && entry?.skip_pickup !== true && !s._pickupRequiredWarned) {
            console.warn('[NiceCalendar] variant requires a pickup the product hides — ignoring pickup_required');
            s._pickupRequiredWarned = true;
          }
          return false;
        }
        if (this._skipPickup()) return false;
        return required;
      },

      _clearPickupSelection() {
        this._state.pickupLocation = null;
        this._state.pickupLocationLabel = '';
      },

      _resetPickupToDefault() {
        this._state.pickupLocation = DEFAULT_PICKUP_LOCATION;
        this._state.pickupLocationLabel = DEFAULT_PICKUP_LOCATION_LABEL;
      },

      // The third-party list that applies right now (spec §3): with an addon
      // selected only that addon's own list — never the main product's, it
      // belongs to a different operator. null = no served list.
      _servedPickupPlaces() {
        const s = this._state;
        const places = s.selectedAddon
          ? s.addonAvailability?.get(s.selectedAddon.addon_product_id)?.pickup_places
          : s.config?.pickup_places;
        return Array.isArray(places) && places.length ? places : null;
      },

      // Active pickup options: the served list, else the catalog API list,
      // else null (= the inline list in the template).
      _pickupOptions() {
        const served = this._servedPickupPlaces();
        if (served) return this._catalogMapping.mapPickupOptions(served);
        const api = this._state.apiPickupOptions;
        return api && api.length ? api : null;
      },

      // Keep the selection inside the active list and remember whether it
      // came from a served list, so a selection whose list has since gone
      // (service degraded, addon fetch failed) falls back to the inline
      // default instead of being posted against the wrong operator. When the
      // product requires a pickup, only a place the customer chose counts —
      // an auto-selected default is cleared (spec 2026-09-11 §4).
      // Returns the active options for the caller to render.
      _syncPickupSelection() {
        const s = this._state;
        const served = this._servedPickupPlaces() !== null;
        const required = this._pickupRequired();
        const options = this._pickupOptions();
        if (options) {
          if (!options.some((o) => o.value === String(s.pickupLocation))) {
            s.pickupChosenByUser = false;
            if (required) {
              this._clearPickupSelection();
            } else {
              s.pickupLocation = Number(options[0].value);
              s.pickupLocationLabel = options[0].label;
            }
          } else if (required && !s.pickupChosenByUser) {
            this._clearPickupSelection();
          }
          s.pickupFromServedList = served;
          return options;
        }
        // The list that served the selection is gone: drop it, the tail below
        // either asks again (required) or falls back to the inline default.
        if (s.pickupFromServedList) {
          s.pickupFromServedList = false;
          s.pickupChosenByUser = false;
          this._clearPickupSelection();
        }
        if (required) {
          if (!s.pickupChosenByUser) this._clearPickupSelection();
        } else if (s.pickupLocation == null) {
          this._resetPickupToDefault();
        }
        return null;
      },

      _clearDropoffSelection() {
        this._state.dropoffLocation = null;
        this._state.dropoffLocationLabel = '';
      },

      // The served dropoff list that applies right now (spec 2026-09-17 §3):
      // with an addon selected only that addon's own list — never the main
      // product's. null = no served list = no dropoff select.
      _servedDropoffPlaces() {
        const s = this._state;
        const places = s.selectedAddon
          ? s.addonAvailability?.get(s.selectedAddon.addon_product_id)?.dropoff_places
          : s.config?.dropoff_places;
        return Array.isArray(places) && places.length ? places : null;
      },

      // The customer must choose a dropoff place (spec 2026-09-17 §3).
      // Deliberately NOT gated on skip_pickup, unlike _pickupRequired():
      // dropoff is independent of pickup and 200650 is a skip_pickup
      // product. The only gate is the presence of the served list.
      _dropoffRequired() {
        const s = this._state;
        if (this._servedDropoffPlaces() === null) return false;
        const entry = s.selectedAddon
          ? s.addonAvailability?.get(s.selectedAddon.addon_product_id)
          : null;
        return (entry?.dropoff_required ?? s.config?.dropoff_required) === true;
      },

      // Keep the selection inside the active dropoff list (spec 2026-09-17
      // §3). Required → only a customer-chosen place counts, auto-selections
      // are cleared; optional → the first entry is auto-selected. Returns
      // the active options for the render; null = render no select.
      _syncDropoffSelection() {
        const s = this._state;
        const served = this._servedDropoffPlaces();
        if (!served) {
          this._clearDropoffSelection();
          s.dropoffChosenByUser = false;
          return null;
        }
        const options = this._catalogMapping.mapPickupOptions(served);
        const required = this._dropoffRequired();
        if (!options.some((o) => o.value === String(s.dropoffLocation))) {
          s.dropoffChosenByUser = false;
          if (required) {
            this._clearDropoffSelection();
          } else {
            s.dropoffLocation = Number(options[0].value);
            s.dropoffLocationLabel = options[0].label;
          }
        } else if (required && !s.dropoffChosenByUser) {
          this._clearDropoffSelection();
        }
        return options;
      },

      // BOOK NOW state shared by _render and _updateBookButton: the first
      // unmet requirement names the button.
      _bookGate() {
        const s = this._state;
        // `loading` drives the spinner; the label is the one the button
        // carries once the in-flight submit resolves.
        if (s.bookingLoading) return { canBook: false, loading: true, label: 'BOOK NOW' };
        if (!s.selectedDate) return { canBook: false, loading: false, label: 'Select a date' };
        if (this._getTotalPersonCount() === 0) return { canBook: false, loading: false, label: 'Select persons' };
        if (!this._areRequiredTextareasFilled()) return { canBook: false, loading: false, label: 'Please fill required fields' };
        if (this._needsStartTimeSelection() && s.selectedStartTime === null) return { canBook: false, loading: false, label: 'Select a time' };
        if (this._pickupRequired() && s.pickupLocation == null) return { canBook: false, loading: false, label: 'Select a pickup location' };
        if (this._dropoffRequired() && s.dropoffLocation == null) return { canBook: false, loading: false, label: 'Select a dropoff location' };
        return { canBook: true, loading: false, label: 'BOOK NOW' };
      },

      // Update only the book button state without full re-render
      _updateBookButton() {
        if (!this._state._hostEl) return;
        const bookBtn = this._state._hostEl.querySelector('button[data-act="book"]');
        if (!bookBtn) return;

        const gate = this._bookGate();
        bookBtn.disabled = !gate.canBook;
        if (gate.loading) {
          bookBtn.classList.add('nt-book-loading');
          bookBtn.innerHTML = `<div class="nt-book-spinner"></div>`;
        } else {
          bookBtn.classList.remove('nt-book-loading');
          bookBtn.textContent = gate.label;
        }
      },
  
      _shiftMonth(delta, host) {
        // Disallow moving into the past
        const t = this._state.today;
        let y = this._state.viewYear;
        let m = this._state.viewMonth + delta;
        if (m < 0) { m = 11; y--; }
        if (m > 11) { m = 0;  y++; }

        // If target (y,m) is before current month, clamp to current month
        const cmp = (y - t.getFullYear()) * 12 + (m - t.getMonth());
        if (cmp < 0) { y = t.getFullYear(); m = t.getMonth(); }

        // If target (y,m) is beyond MAX_VISIBLE_MONTHS from now, clamp to limit
        const maxMonthsFromNow = new Date(
          t.getFullYear(),
          t.getMonth() + (MAX_VISIBLE_MONTHS - 1),
          1
        );
        const targetDate = new Date(y, m, 1);
        if (targetDate.getTime() > maxMonthsFromNow.getTime()) {
          y = maxMonthsFromNow.getFullYear();
          m = maxMonthsFromNow.getMonth();
        }

        this._state.viewYear = y;
        this._state.viewMonth = m;
        this._state.selectedDate = null;
        this._state.selectedStartTime = null;

        this._scheduleFetch(y, m);
        this._render(host);
      },
  
      _toYMD(date) { return `${date.getFullYear()}-${this._pad(date.getMonth()+1)}-${this._pad(date.getDate())}`; },

      // Inclusive on both ends; a null/empty bound is an open one.
      _ymdInRange(ymd, from, to) { return (!from || from <= ymd) && (!to || ymd <= to); },

      // Helper to check if start time selection is needed for the selected date
      _needsStartTimeSelection() {
        const s = this._state;
        if (!s.selectedDate) return false;
        const ymd = this._toYMD(s.selectedDate);

        // Use filtered start times (only slots with capacity/availability > 0)
        const startTimes = this._getStartTimesForDate(ymd);
        return Array.isArray(startTimes) && startTimes.length > 1;
      },
      
      // Helper to get start times for a given date (filtered - only slots with capacity > 0)
      _getStartTimesForDate(ymd) {
        const s = this._state;

        const filterSlots = (slots) => {
          if (!Array.isArray(slots)) return [];
          return slots.filter((slot) => {
            // Frontend DayAvailability.start_times use `capacity`
            // (backend cache slots use `availability`, but those are not sent here)
            const cap = typeof slot.capacity === 'number'
              ? slot.capacity
              : (typeof slot.availability === 'number' ? slot.availability : 0);
            return cap > 0;
          });
        };
        
        // Check addon first if selected
        if (s.selectedAddon) {
          const addonAvailability = s.addonAvailability.get(s.selectedAddon.addon_product_id);
          return filterSlots(addonAvailability?.start_times);
        }
        
        // Otherwise return main product start times
        return filterSlots(s.dayStartTimesMap.get(ymd));
      },
      
      // Helper to get ALL start times for a given date (unfiltered - includes slots with 0 capacity)
      _getAllStartTimesForDate(ymd) {
        const s = this._state;
        
        // Check addon first if selected
        if (s.selectedAddon) {
          const addonAvailability = s.addonAvailability.get(s.selectedAddon.addon_product_id);
          return Array.isArray(addonAvailability?.start_times) ? addonAvailability.start_times : [];
        }
        
        // Otherwise return main product start times
        return Array.isArray(s.dayStartTimesMap.get(ymd)) ? s.dayStartTimesMap.get(ymd) : [];
      },
  
      // Spec 2026-10-08 B3: the commerce cart's form-post URL, or null for
      // WooCommerce (no form action = the product page, exactly as today).
      // https only; http just for localhost (local development). A URL that
      // carries credentials is refused, mirroring the server-side rule.
      _commerceCartAction() {
        const cfg = this._state.config;
        if (cfg.cart_target !== 'commerce') return null;
        // Told to use the commerce cart but handed nothing usable: stay on
        // WooCommerce and say so once per init (never print the URL itself).
        const unusable = () => {
          if (!this._state._cartUrlWarned) {
            console.warn('[NiceCalendar] cart_target "commerce" but cart_url is unusable — posting to WooCommerce');
            this._state._cartUrlWarned = true;
          }
          return null;
        };
        if (typeof cfg.cart_url !== 'string') return unusable();
        let url;
        try {
          url = new URL(cfg.cart_url);
        } catch (e) {
          return unusable();
        }
        if (url.username || url.password) return unusable();
        const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
        if (url.protocol === 'https:' || (url.protocol === 'http:' && local)) return url.href;
        return unusable();
      },

      _book() {
        const s = this._state, cfg = s.config;
        if (!s.selectedDate) return;
        
        // Check if at least 1 person is selected
        if (this._getTotalPersonCount() === 0) {
          console.warn('[NiceCalendar] Cannot book: no persons selected');
          return;
        }
        
        // Check if all required textareas are filled
        if (!this._areRequiredTextareasFilled()) {
          console.warn('[NiceCalendar] Cannot book: required textarea fields are not filled');
          return;
        }

        // Settle the selection against the active list before the guard reads
        // it (the loading _render below syncs again for the posted fields).
        this._syncPickupSelection();
        // A required pickup can never reach WooCommerce empty (spec 2026-09-11 §4)
        if (this._pickupRequired() && s.pickupLocation == null) {
          console.warn('[NiceCalendar] Cannot book: pickup location is required');
          return;
        }
        this._syncDropoffSelection();
        // A required dropoff can never reach WooCommerce empty (spec 2026-09-17 §3)
        if (this._dropoffRequired() && s.dropoffLocation == null) {
          console.warn('[NiceCalendar] Cannot book: dropoff location is required');
          return;
        }

        // Prevent double-clicks and show loading state
        if (s.bookingLoading) return;
        
        // Set loading state and re-render button
        s.bookingLoading = true;
        this._render(this._state._hostEl);
  
        // Get availability count - use addon's capacity if addon is selected, otherwise use main product
        let avlCnt;
        if (s.selectedAddon) {
          const addonAvailability = s.addonAvailability.get(s.selectedAddon.addon_product_id);
          avlCnt = addonAvailability?.capacity || 0;
        } else {
          const cap = this._capacityFor(s.selectedDate.getFullYear(), s.selectedDate.getMonth(), s.selectedDate.getDate());
          avlCnt = (cap == null) ? (cfg.availabilityCount ?? 999) : cap;
        }
  
        const form = document.createElement('form');
        form.method = 'POST';
        const cartAction = this._commerceCartAction();
        if (cartAction) form.setAttribute('action', cartAction);
  
        form.appendChild(this._hidden('wc_bookings_field_start_date_month', this._pad(s.selectedDate.getMonth()+1)));
        form.appendChild(this._hidden('wc_bookings_field_start_date_day', this._pad(s.selectedDate.getDate())));
        form.appendChild(this._hidden('wc_bookings_field_start_date_year', String(s.selectedDate.getFullYear())));
  
        // Add person field counts to form
        this._getCurrentPersonFields().forEach(field => {
          const count = s.personCounts[field.fieldId] || 0;
          if (field.fieldId != null) {
            form.appendChild(this._hidden(`wc_bookings_field_persons_${field.fieldId}`, String(count)));
          }
        });
  
        form.appendChild(this._hidden('avl_cnt', String(avlCnt)));
        
        // Use selected start time if available, otherwise use addon's or main product's time_id
        let timeId = cfg.timeId;
        let bokunId = cfg.bokunId;
        
        // When addon is selected, always use addon's bokun_id (variant has different bokun_id than main product)
        if (s.selectedAddon) {
          const availability = s.addonAvailability.get(s.selectedAddon.addon_product_id);
          if (availability) {
            bokunId = availability.bokun_id || cfg.bokunId;
            timeId = availability.time_id || cfg.timeId;
          }
        }
        // Priority for timeId: selectedStartTime > addon availability > config
        if (s.selectedStartTime) {
          timeId = s.selectedStartTime.start_time_id;
        }
        
        // Add addon product information - "YES" if product has addons, "NO" if not
        const hasAddons = cfg.productAddons && cfg.productAddons.length > 0;
        form.appendChild(this._hidden('addon_product', hasAddons ? 'YES' : 'NO'));
        
        if (timeId != null)      form.appendChild(this._hidden('time_id', String(timeId)));
        if (bokunId != null)     form.appendChild(this._hidden('bokun_id', String(bokunId)));
  
        // Add multiday_tours_time_ids if available (use addon's if addon is selected, otherwise use main product's)
        let multidayToursTimeIds = cfg.multiday_tours_time_ids;
        if (s.selectedAddon) {
          const availability = s.addonAvailability.get(s.selectedAddon.addon_product_id);
          if (availability && availability.multiday_tours_time_ids) {
            multidayToursTimeIds = availability.multiday_tours_time_ids;
          }
        }
        if (multidayToursTimeIds != null) {
          try {
            form.appendChild(this._hidden('multiday_tours_time_ids', JSON.stringify(multidayToursTimeIds)));
          } catch (e) {
            console.warn('[NiceCalendar] multiday_tours_time_ids stringify failed', e);
          }
        }
  
        form.appendChild(this._hidden('main_product', String(cfg.productId)));
        
        // Use selected addon's product ID for add-to-cart if addon is selected
        const addToCartProductId = s.selectedAddon ? s.selectedAddon.addon_product_id : cfg.productId;
        form.appendChild(this._hidden('add-to-cart', String(addToCartProductId)));
  
        // Only include pickup fields when product/addon does not have skip_pickup
        const skipPickup = this._skipPickup();
        if (!skipPickup) {
          // The label only travels with the id it describes — an empty
          // pickup_content on its own tells WooCommerce nothing.
          if (s.pickupLocation != null) {
            form.appendChild(this._hidden('pickup', String(s.pickupLocation)));
            form.appendChild(this._hidden('pickup_content', s.pickupLocationLabel));
          }
        }
        // Dropoff travels only when a served list is active (spec 2026-09-17 §3);
        // the label only travels with the id it describes.
        if (this._servedDropoffPlaces() && s.dropoffLocation != null) {
          form.appendChild(this._hidden('dropoff', String(s.dropoffLocation)));
          form.appendChild(this._hidden('dropoff_content', s.dropoffLocationLabel));
        }
        // Post a deposit only when this product offers one for this date
        // (spec 2026-09-11 §4) — a choice made for another date must not leak.
        const depositValue = this._depositOffered() && s.depositOption === 'deposit' ? 'yes' : 'no';
        form.appendChild(this._hidden('awcdp_deposit_option', depositValue));
  
        if (cfg.gtmProductData) {
          try { form.appendChild(this._hidden('gtm4wp_product_data', JSON.stringify(cfg.gtmProductData))); }
          catch(e){ console.warn('[NiceCalendar] gtm4wp_product_data stringify failed', e); }
        }
  
        // Add extras data to form
        this._addExtrasToForm(form);

        try {
          document.body.appendChild(form);
          form.submit();
        } catch (error) {
          console.error('[NiceCalendar] Booking submission failed:', error);
          // Reset loading state on error
          s.bookingLoading = false;
          this._render(this._state._hostEl);
        }
      },
  
      // Add extras data to form submission
      _addExtrasToForm(form) {
        if (!this._state.config.extras) return;

        // Check if extras config contains "No WCPA fields found" message
        if (typeof this._state.config.extras === 'string' && 
            this._state.config.extras.includes('No WCPA fields found for this product')) {
          console.log('[NiceCalendar] Skipping extras form data - no WCPA fields found');
          return;
        }

        // Check if extras config is an object but has the error message
        if (typeof this._state.config.extras === 'object' && 
            this._state.config.extras.message && 
            this._state.config.extras.message.includes('No WCPA fields found for this product')) {
          console.log('[NiceCalendar] Skipping extras form data - no WCPA fields found');
          return;
        }
        
        // Check for "WCPA plugin not active" error
        if (typeof this._state.config.extras === 'object' && 
            this._state.config.extras.error && 
            this._state.config.extras.error.includes('WCPA plugin not active')) {
          console.log('[NiceCalendar] Skipping extras form data - WCPA plugin not active');
          return;
        }
  
        // Add wcpa_field_key_checker
        form.appendChild(this._hidden('wcpa_field_key_checker', 'wcpa'));
  
        // Add vi-hidden fields for each field set in the extras configuration
        const personFields = this._getCurrentPersonFields();
        this._state.config.extras.fields_sets.forEach((fieldSet, fieldSetIndex) => {
          personFields.forEach(personField => {
            const count = this._state.personCounts[personField.fieldId] || 0;
            // Always use the count value, including 0
            const value = String(count);
            const fieldName = 'vi-hidden-' + personField.viPersonType;
            console.log('[Form] Adding vi-hidden field for field set', fieldSetIndex + 1, ':', fieldName, '=', value);
            form.appendChild(this._hidden(fieldName, value));
          });
          
          // Always add vi-hidden-teenager field with actual value if available
          const teenagerField = personFields.find(pf => pf.viPersonType === 'teenager');
          if (teenagerField) {
            const teenagerCount = this._state.personCounts[teenagerField.fieldId] || 0;
            form.appendChild(this._hidden('vi-hidden-teenager', String(teenagerCount)));
            console.log('[Form] Adding vi-hidden-teenager field for field set', fieldSetIndex + 1, ':', 'vi-hidden-teenager', '=', teenagerCount);
          } else {
            // If no teenager field in current product, add with empty value
            form.appendChild(this._hidden('vi-hidden-teenager', ''));
            console.log('[Form] Adding vi-hidden-teenager field for field set', fieldSetIndex + 1, ':', 'vi-hidden-teenager', '=', '');
          }
        });
  
        this._state.config.extras.fields_sets.forEach(fieldSet => {
          // Skip empty field sets
          if (!fieldSet.fields || fieldSet.fields.length === 0) {
            return;
          }
          
          // Check if this field set has any selected extras or textarea fields
          const hasSelectedExtras = fieldSet.fields.some(field => {
            if (field.type === 'checkbox-group') {
              const fieldValue = this._state.extrasValues.get(field.fieldId);
              return fieldValue && (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue);
            }
            if (field.type === 'textarea') {
              // Always process textarea fields (they may be required)
              return true;
            }
            return false;
          });
          
          // Only process fields from field sets that have selected extras or textarea fields
          if (!hasSelectedExtras) {
            console.log('[Form] Skipping field set with no selected extras or textarea fields');
            return;
          }
          
          fieldSet.fields.forEach(field => {
            const fieldValue = this._state.extrasValues.get(field.fieldId);
            
            console.log('[Form] Processing field:', {
              type: field.type,
              name: field.name,
              fieldId: field.fieldId,
              label: field.label
            });
            
            if (field.type === 'checkbox-group' && fieldValue) {
              // Handle checkbox group - add each selected option
              if (Array.isArray(fieldValue)) {
                fieldValue.forEach((value, index) => {
                  form.appendChild(this._hidden(`${field.name}[${index}]`, value));
                });
              } else {
                form.appendChild(this._hidden(`${field.name}[0]`, fieldValue));
              }
            } else if (field.type === 'select') {
              // Handle select fields - get value from occupancy data
              let selectValue = fieldValue || '';
              
              // If this is a select field for extras occupancy, get the value from occupancy data
              // Check if this is a pricing select field (has price and numeric options)
              if (field.price && field.options && field.options.length > 0 && 
                  field.options.every(opt => !isNaN(parseInt(opt.value)))) {
                
                // This is a pricing select field - get the occupancy count for the corresponding person type
                const checkboxField = fieldSet.fields.find(f => f.type === 'checkbox-group');
                if (checkboxField) {
                  const checkboxValue = this._state.extrasValues.get(checkboxField.fieldId);
                  if (checkboxValue && Array.isArray(checkboxValue)) {
                    checkboxValue.forEach(value => {
                      const occupancyKey = `${checkboxField.fieldId}_${value}`;
                      const occupancy = this._state.extrasOccupancy.get(occupancyKey);
                      if (occupancy) {
                        // Determine person type from field label
                        let personType = '';
                        if (field.label && field.label.toLowerCase().includes('adult')) {
                          personType = 'adults';
                        } else if (field.label && field.label.toLowerCase().includes('teenager')) {
                          personType = 'teenager';
                        } else if (field.label && field.label.toLowerCase().includes('children')) {
                          personType = 'children';
                        }
                        
                        if (personType) {
                          const count = occupancy.get(personType) || 0;
                          if (count > 0) {
                            selectValue = String(count);
                          }
                        }
                      }
                    });
                  }
                }
              }
              
              // Handle shoe size select fields separately
              if (field.label && field.label.toLowerCase().includes('size') && field.label.includes('#')) {
                // This is a shoe size select field
                console.log('[Form] Processing size field:', field.name, field.fieldId);
                console.log('[Form] Current extrasShoeSizes:', this._state.extrasShoeSizes);
                
                // Direct lookup using field ID as key
                const sizeValue = this._state.extrasShoeSizes.get(field.fieldId);
                if (sizeValue) {
                  console.log('[Form] Found size value for field:', field.name, '=', sizeValue);
                  form.appendChild(this._hidden(field.name, sizeValue));
                } else {
                  console.log('[Form] No size value found for field:', field.name, field.fieldId);
                  // Still add the field even if no value to ensure it's in the form
                  form.appendChild(this._hidden(field.name, ''));
                }
              } else {
                // This is a regular select field (pricing or other)
                // Only add to form if it has a meaningful value (greater than 0 for person type fields)
                if (selectValue && selectValue !== '0' && selectValue !== '') {
                  console.log('[Form] Adding select field:', field.name, '=', selectValue);
                  form.appendChild(this._hidden(field.name, selectValue));
                } else {
                  console.log('[Form] Skipping empty select field:', field.name, '=', selectValue);
                }
              }
            } else if (field.type === 'textarea') {
              // Handle textarea fields
              const textareaValue = fieldValue || '';
              console.log('[Form] Adding textarea field:', field.name, '=', textareaValue);
              form.appendChild(this._hidden(field.name, textareaValue));
            } else if (field.type === 'hidden') {
              // Skip vi-hidden fields since we already added them at the beginning
              if (field.name.startsWith('vi-hidden-')) {
                console.log('[Form] Skipping vi-hidden field from field set:', field.name, '(already added at beginning)');
                return;
              } else {
                form.appendChild(this._hidden(field.name, field.value || ''));
              }
            }
          });
        });
      },
  
      // --- Public init ---
      /* ===== Catalog API → internal config mapping (PLAN §5.14) =====
         Clean /catalog/products/:id shapes in; the shortcode-era shapes
         the rest of this file consumes out. This lives in the widget on
         purpose — the API never speaks the legacy vocabulary, and this
         layer is deleted together with the legacy add-to-cart POST when
         the phase-3 cart API lands. */
      _catalogMapping: {
        _mapPersonTypes(personTypes) {
          return (personTypes || []).map((p) => ({
            fieldId: p.id,
            label: p.label,
            price: String(p.priceIsk),
            viPersonType: p.semanticType != null ? p.semanticType : '',
            min: p.minCount,
            max: p.maxCount != null ? p.maxCount : '',
          }));
        },
        _mapExtras(detail) {
          const rows = detail.extras || [];
          if (rows.length === 0) return undefined;
          const sections = [];
          const byForm = new Map();
          const usedFieldIds = new Set();
          // Rows arrive ordered (sortOrder, id) from the API; section order
          // and in-section field order both follow it.
          for (const row of rows) {
            const cfg = row.config || {};
            const formKey = String(cfg.formId != null ? cfg.formId : '');
            if (!byForm.has(formKey)) {
              const section = {
                section_key: formKey,
                section_name: cfg.formTitle || '',
                section_label: cfg.formTitle || '',
                fields: [],
              };
              byForm.set(formKey, section);
              sections.push(section);
            }
            // Conditional-logic cl_field rules reference bare elementIds, so
            // the bare id is the fieldId; if two forms on one product ever
            // carry the same elementId (cloned forms), later ones fall back
            // to the unique formId:elementId key deterministically.
            const bareId = cfg.elementId || row.fieldKey;
            const fieldId = usedFieldIds.has(bareId) ? row.fieldKey : bareId;
            if (fieldId !== bareId) {
              console.warn(
                `[NiceCalendar] duplicate extras elementId ${bareId} on product ${detail.id} — using ${fieldId}`,
              );
            }
            usedFieldIds.add(fieldId);
            const field = {
              fieldId,
              name: cfg.name != null ? cfg.name : '',
              label: row.label,
              type: row.fieldType,
              value: cfg.value != null ? cfg.value : '',
              required: row.required === true,
              price: cfg.price != null ? cfg.price : 0,
            };
            if (Array.isArray(row.options) && row.options.length > 0) {
              field.options = row.options.map((o) => ({
                label: o.label,
                value: o.value,
                price: o.priceIsk != null ? o.priceIsk : '',
              }));
            }
            const cl = row.conditionalLogic;
            // Gate on enableCl exactly like [nt_product_fields_json] does —
            // relations without enableCl emit no conditional_logic.
            if (cl && cl.enableCl) {
              field.conditional_logic = {
                enabled: true,
                relations: cl.relations || [],
                rule: cl.clRule || '',
              };
            }
            byForm.get(formKey).fields.push(field);
          }
          return {
            product_id: detail.id,
            product_name: detail.name,
            fields_sets: sections,
          };
        },
        mapConfig(detail, variantDetailsById) {
          const personFields = this._mapPersonTypes(detail.personTypes);
          const productAddons = [...(detail.variants || [])]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .filter((v) => v.variantProductId != null)
            .map((v) => {
              // The parent lists itself as a variant — its data is the
              // already-fetched main detail, no extra request.
              const variantDetail =
                v.variantProductId === detail.id
                  ? detail
                  : variantDetailsById.get(v.variantProductId);
              if (!variantDetail) return null;
              return {
                addon_product_id: v.legacyProductId,
                addon_product_name: variantDetail.name,
                addon_title: v.label,
                person_types: this._mapPersonTypes(variantDetail.personTypes),
              };
            })
            .filter((addon) => addon !== null);
          const prices = detail.prices || {};
          const bookingPricing = {
            product_id: detail.id,
            product_name: detail.name,
            base_cost: prices.baseIsk != null ? prices.baseIsk : 0,
            block_cost: 0,
            display_cost:
              prices.displayFromIsk != null ? prices.displayFromIsk : null,
            // Array order preserved — rule precedence is last-match-wins.
            pricing_ranges: (detail.seasonalPricing || []).map((r) => ({
              type: r.ruleType,
              from: r.fromDate != null ? r.fromDate : '',
              to: r.toDate != null ? r.toDate : '',
              cost: r.costAmount,
              modifier: r.costModifier,
              base_cost: r.baseCostAmount,
              base_modifier: r.baseCostModifier,
            })),
          };
          const result = { personFields, productAddons, bookingPricing };
          const extras = this._mapExtras(detail);
          if (extras !== undefined) result.extras = extras;
          return result;
        },
        mapPickupOptions(places) {
          return (places || []).map((p) => ({
            value: String(p.id),
            label: p.name,
          }));
        },
      },

      async _loadCatalogConfig(explicit) {
        const cfg = this._state.config;
        const base = cfg.catalogBase || cfg.apiBase;
        const getJson = async (path) => {
          const resp = await fetch(`${base}${path}`, {
            headers: cfg.apiHeaders || {},
          });
          if (!resp.ok) {
            throw new Error(`Catalog fetch failed [${resp.status}]: ${path}`);
          }
          return resp.json();
        };
        try {
          const [detail, places] = await Promise.all([
            getJson(`/catalog/products/${encodeURIComponent(cfg.productId)}`),
            getJson('/catalog/pickup-places'),
          ]);
          // Variant person types live on the variants' own catalog rows;
          // the parent's self-row needs no extra request.
          const variantIds = (detail.variants || [])
            .filter(
              (v) => v.variantProductId != null && v.variantProductId !== detail.id,
            )
            .map((v) => v.variantProductId);
          const variantDetails = await Promise.all(
            variantIds.map((id) => getJson(`/catalog/products/${id}`)),
          );
          const variantDetailsById = new Map(
            variantDetails.map((d) => [d.id, d]),
          );
          const mapped = this._catalogMapping.mapConfig(
            detail,
            variantDetailsById,
          );
          for (const key of [
            'personFields',
            'productAddons',
            'extras',
            'bookingPricing',
          ]) {
            if (!(key in explicit) && key in mapped) cfg[key] = mapped[key];
          }
          this._state.apiPickupOptions =
            this._catalogMapping.mapPickupOptions(places);
        } catch (e) {
          console.error(
            '[NiceCalendar] catalog config fetch failed — using inline config',
            e,
          );
        }
      },

      async init(config) {
        const defaults = {
          mount: '#nt-calendar',
          apiHeaders: {},
          // Availability API origin (was hardcoded twice, PLAN §5.2). Render
          // since the nice-inventory Render move, Phase 4 (spec 2026-10-14).
          apiBase: 'https://availability.nicetravel.is',
          // Retried once on a network error or 5xx from apiBase: the old
          // origin (Mau), fresh until the move's Phase 5 points it at Render
          // too — then this can go (null = no retry).
          apiFallbackBase: 'https://inventory.nicetravel.is',
          // Catalog API origin; null = same as apiBase. Only used when
          // configSource is 'api'.
          catalogBase: null,
          // 'api' = fetch person types/addons/extras/pricing + pickup
          // places from the catalog API; anything else = inline config
          // exactly as today.
          configSource: null,
          productId: null,
          // Currency conversion settings
          currency: 'ISK', // Target currency for display (e.g., 'USD', 'EUR', 'GBP')
          currencyRate: 1, // Exchange rate from ISK to target currency (example: 1 ISK = 0.0072 USD)
          locale: 'en-US', // Locale for the target currency formatting
          personFields: [], // Dynamic person field configuration
          productAddons: [], // Product addons configuration
          extras: null, // Extras configuration
          bookingPricing: null, // WooCommerce Bookings pricing metadata (optional)
  
          // POST extras (unchanged)
          timeId: null,
          bokunId: null,
          skip_pickup: false,
          // Spec 2026-09-11 §4: read-time defaults = today's behaviour until
          // /availability says otherwise.
          deposit: { offered: true, from: null, to: null },
          pickup_required: false,
          // Spec 2026-09-17 §3: dropoff has no inline form; both stay inert
          // until /availability serves a list.
          dropoff_places: null,
          dropoff_required: false,
          same_day_booking: false,
          // Spec 2026-10-08 B3: WooCommerce until /availability says otherwise.
          cart_target: 'woocommerce',
          cart_url: null
        };
        this._state.config = Object.assign({}, defaults, config || {});

        // Re-init always starts from a clean API-options slate; API-config
        // mode below repopulates it on success.
        this._state.apiPickupOptions = undefined;

        // A fresh init starts from the inline default: a selection from a
        // previous init (tests re-init the same object) must not leak.
        this._resetPickupToDefault();
        this._state.pickupFromServedList = false;
        this._state.pickupChosenByUser = false;
        this._state.dropoffLocation = null;
        this._state.dropoffLocationLabel = '';
        this._state.dropoffChosenByUser = false;
        this._state._pickupRequiredWarned = false;
        this._state._depositRuleWarned = false;
        this._state._cartUrlWarned = false;
        this._state.depositOption = 'full';
        // _book() sets this and only the error path clears it (the page navigates on submit).
        this._state.bookingLoading = false;

        // ===== API-config mode (PLAN §5.14): fetch what the shortcodes
        // inline today from the clean catalog API. Explicitly passed inline
        // values win; any fetch failure falls back to inline config. =====
        if (
          this._state.config.configSource === 'api' &&
          this._state.config.productId != null
        ) {
          await this._loadCatalogConfig(config || {});
        }

        // Initialize person field counts and base prices
        this._state.personCounts = {};
        this._state.basePersonPrices = {};
        this._state.config.personFields.forEach(field => {
          this._state.personCounts[field.fieldId] = parseInt(field.min) || 0;
          this._state.basePersonPrices[field.fieldId] = parseInt(field.price) || 0;
        });
  
        // Initialize addon selection and availability
        this._state.selectedAddon = null;
        this._state.selectedStartTime = null;
        this._state.addonAvailability = new Map(); // Map of addon_id -> availability data
        this._state.monthAddonData = new Map(); // Map of addon_id -> full month data
        this._state.addonLoading = false;
  
          // Initialize extras state
          this._state.extrasValues = new Map(); // Map of fieldId -> value
          this._state.extrasVisibility = new Map(); // Map of fieldId -> visible
          this._state.extrasOccupancy = new Map(); // Map of fieldId -> Map of personType -> count
          this._state.extrasShoeSizes = new Map(); // Map of sizeKey -> selected size
  
        const t = new Date();
        this._state.today = t; // ensure current "today"
        this._state.viewYear = t.getFullYear();
        this._state.viewMonth = t.getMonth();
  
        const mountEl = document.querySelector(this._state.config.mount);
        if (!mountEl) { console.error('[NiceCalendar] mount element not found:', this._state.config.mount); return; }
  
        const root = mountEl.attachShadow ? mountEl.attachShadow({ mode: 'open' }) : mountEl;
        const style = document.createElement('style'); style.textContent = this._style(); root.appendChild(style);
        const host = document.createElement('div'); root.appendChild(host);
        this._state._hostEl = host;
  
        // Debounced fetch for the initial month (fires after 2s)
        this._scheduleFetch(this._state.viewYear, this._state.viewMonth);
        this._render(host);
        
        // Start monitoring currency cookie changes
        this._startCurrencyMonitoring();
        
        // Clean up timers when page unloads
        const cleanupTimer = () => this._stopCurrencyMonitoring();
        window.addEventListener('beforeunload', cleanupTimer);
        window.addEventListener('unload', cleanupTimer);
      }
    };
  
    window.NiceCalendar = NiceCalendar;
  })();